import {MSB_FONT_FILES} from '../../lib/msb-font-files';
import {TEXT} from './text-colors';
export {TEXT} from './text-colors';
// MSB(마스터 스토리보드) V2 디자인 토큰 — 실행 정본 (Remotion 런타임).
// 원본: MSB 인수인계 tokens/default.tsx (V2.33) 파일 복사 후 폰트 블록만 crabs 관용구로 교체.
//   - 원본 폰트(HyundaiHarmonie 로컬 TTF fetch)는 미보유 → @remotion/google-fonts NotoSansKR 로 대체.
//   - 그 외 토큰·컴포넌트·수치는 원본 그대로 (프레임워크 §8-0 하드룰의 물리적 기반 — 임의 변경 금지).
// 선택용 메타 카탈로그(프롬프트 부록·검증)는 supabase/functions/_shared/msb/catalog.ts —
//   두 파일의 키 정합은 src/remotion/msb/catalog-sync.test.ts 가 가드한다.
// Usage: import { T, F, FW, LINE_HEIGHT, KO_TEXT, STATUS, TEXT, BG, LAYOUT, AI_WATERMARK, CARD, BADGE, GLOW, TRANSITION, EASING, STAGGER, SPRING, PATTERN, ATMOSPHERE_A, ATMOSPHERE_B, VFX_CATALOG, AtmosphereLayer, VfxLayer, RevealAccent, PERSONA_CATALOG, resolvePersonaColors, filterPersonas } from './tokens'
import React from 'react';
import { AbsoluteFill, Easing, interpolate, staticFile } from 'remotion';
import { loadFont as loadFontNotoSansKR } from '@remotion/google-fonts/NotoSansKR';

// ─── Font Loading ─────────────────────────────────────────────
// 🔤 Paperlogy 전 웨이트 self-host(회장님 07-16 선택 — 유튜브 모던 룩) + NotoSansKR 폴백 유지
//   (페이퍼로지 미커버 글리프·로드 실패 대비). FontFace 관용구=자막 폰트 계약과 동일(웹 렌더 OK).
// FontFace 가드 — vitest(jsdom)/node 환경에서 모듈 임포트만으로 FontFace 접근이 터지지 않게.
const _canLoadFonts = typeof FontFace !== 'undefined' && typeof document !== 'undefined';
const _notoHandle = _canLoadFonts ? loadFontNotoSansKR() : null;
const _paperlogyReady: Promise<unknown> = _canLoadFonts
  ? Promise.all(MSB_FONT_FILES.map(({file, weight}) => {
      const face = new FontFace('Paperlogy', `url(${staticFile(file)})`, { weight: String(weight) });
      return face.load().then((f) => { document.fonts.add(f); }).catch(() => {}); // 실패=Noto 폴백
    }))
  : Promise.resolve([]);
export const msbFontsReady: Promise<unknown> = Promise.all([
  _paperlogyReady,
  Promise.resolve(_notoHandle?.waitUntilDone?.()).catch(() => {}),
]).catch(() => {});

// ─── Typography (7-step, HY series baseline) ──────────────────
// Grade mapping: A★ → hero/display · A → title/header · B → body/caption · C → caption/label
// 1920×1080 기준. label(36) 이하 사용 금지
export const T = {
  label:   40,
  caption: 44,
  body:    56,
  header:  72,
  title:  108,
  hero:   132,
  display:164,
} as const;

// ─── Font ─────────────────────────────────────────────────────
export const F = {
  ui: "Paperlogy, 'Noto Sans KR', sans-serif",
} as const;

// ─── Font Weight ──────────────────────────────────────────────
// 사이즈↔weight 매핑 및 사용 규칙은 MASTER_STORYBOARD_FRAMEWORK_V2.md §9-2 참조
// 한 씬에서 FW.extrabold, FW.bold는 각 최대 1회 (가장 큰 사이즈 우선)
export const FW = {
  extrabold: 800,
  bold:      700,
  semibold:  600,
  medium:    500,
  regular:   400,
} as const;

// ─── Line Height ──────────────────────────────────────────────
// 텍스트 행간 토큰. 직접 숫자 기입 금지 — 반드시 이 키 lookup
// tight   (1.2): hero/title/display 큰 사이즈 (T.hero·T.display·T.title)
// normal  (1.5): 본문 기본 (T.body·T.caption·T.label) — 모든 일반 텍스트의 기본값
// loose   (1.7): 카드 내 목록·강조 가독성용 (list/번호 매기기 항목)
// 사용: <div style={{ lineHeight: LINE_HEIGHT.normal }}>
export const LINE_HEIGHT = {
  tight:  1.2,
  normal: 1.5,
  loose:  1.7,
} as const;

// ─── Semantic color — Status (MGD §6-1) ───────────────────────
// STATUS = 어두운 배경 기본값 (하위 호환)
// STATUS_LIGHT = 밝은 배경용
// resolveStatus(isDark) → 배경에 따라 올바른 세트 반환
export const STATUS = {
  // ── 안전 (Success) — dark surface
  success:     '#16A34A',
  successText: '#4ADE80',
  successGlow: 'rgba(34,197,94,0.30)',
  // ── 위험 (Danger) — dark surface
  danger:      '#DC2626',
  dangerText:  '#F87171',
  dangerGlow:  'rgba(239,68,68,0.30)',
  // ── 주의 (Warning) — dark surface
  warning:     '#D97706',
  warningText: '#FCD34D',
  warningGlow: 'rgba(245,158,11,0.30)',
  // ── 정보 (Info) — dark surface
  info:        '#2563EB',
  infoText:    '#93C5FD',
  infoGlow:    'rgba(37,99,235,0.30)',
  // ── 비활성 (Neutral) — dark surface
  neutral:     '#475569',
  neutralText: '#94A3B8',
} as const;

// 밝은 배경 전용 STATUS (어두운 색조)
export const STATUS_LIGHT = {
  success:     '#14532D',
  successText: '#15803D',
  successGlow: 'rgba(21,128,61,0.20)',
  danger:      '#991B1B',
  dangerText:  '#B91C1C',
  dangerGlow:  'rgba(153,27,27,0.20)',
  warning:     '#92400E',
  warningText: '#B45309',
  warningGlow: 'rgba(146,64,14,0.18)',
  info:        '#1E3A8A',
  infoText:    '#1D4ED8',
  infoGlow:    'rgba(30,58,138,0.18)',
  neutral:     '#334155',
  neutralText: '#475569',
} as const;

// 배경에 따라 올바른 STATUS 세트 반환
export const resolveStatus = (isDark: boolean) => isDark ? STATUS : STATUS_LIGHT;

// ─── Semantic color — Text (MGD §6-1) ─────────────────────────


// ─── Color helpers (private) ─────────────────────────────────
const _rgb = (h: string): [number,number,number] => {
  const s = h.replace('#','');
  return [parseInt(s.slice(0,2),16), parseInt(s.slice(2,4),16), parseInt(s.slice(4,6),16)];
};
const _hex = (r:number,g:number,b:number) =>
  '#'+[r,g,b].map(v=>Math.max(0,Math.min(255,Math.round(v))).toString(16).padStart(2,'0')).join('');
const _mix = (h:string,t:number)=>{ const[r,g,b]=_rgb(h); return _hex(r+(255-r)*t, g+(255-g)*t, b+(255-b)*t); };
const _dim = (h:string,t:number)=>{ const[r,g,b]=_rgb(h); return _hex(r*(1-t), g*(1-t), b*(1-t)); };
const _a   = (h:string,op:number)=> h+Math.round(op*255).toString(16).padStart(2,'0');

// ─── BG_PALETTE — 씬 base 컬러 팔레트 ───────────────────────────
// 여기서 베이스 하나를 선택 → BG[key] 또는 mkBg()로 전체 세트 파생
// 사용: BG.navy.base  /  mkBg(BG_PALETTE.dusk).panel
export const BG_PALETTE = {
  // ── Cool blue dark
  navy:        '#0C1220',   // 쿨 네이비 (default)
  midnight:    '#070A14',   // 더 깊은 블루블랙
  deep:        '#04060E',   // 시네마틱 블랙블루
  // ── Neutral dark
  slate:       '#0F172A',   // 채도 낮은 블루그레이
  charcoal:    '#0D0D12',   // 거의 무채색 다크
  ink:         '#0A0A0E',   // 순 다크
  // ── Warm dark
  dusk:        '#100D1E',   // 바이올렛 다크
  ember:       '#160C0A',   // 레드-브라운 다크
  umber:       '#14100A',   // 웜 브라운 다크
  // ── Accent-tinted dark (각 ACCENT 키와 페어링)
  blueNight:   '#081428',   // 블루 액센트 씬용
  violetNight: '#0E0820',   // 바이올렛 액센트 씬용
  greenNight:  '#07110C',   // 그린 액센트 씬용
  // ── Light neutral
  white:       '#FFFFFF',   // 순백
  cloud:       '#F8FAFC',   // 쿨 오프화이트 (기본 라이트)
  pearl:       '#F1F5F9',   // 블루-화이트
  silver:      '#E2E8F0',   // 라이트 쿨그레이
  // ── Light warm
  linen:       '#FEF6E4',   // 웜 오프화이트 (앰버·골드 배경)
  cream:       '#FFF7ED',   // 소프트 오렌지 화이트
  blush:       '#FDF2F8',   // 소프트 핑크-화이트
  // ── Light accent-tinted
  iceBlue:     '#EFF6FF',   // 블루 액센트 라이트 씬용
  mintGreen:   '#F0FDF4',   // 그린 액센트 라이트 씬용
} as const;

// mkBg: base hex → { impact · base · panel · elevated · light · surface }
// dark base:  impact=어두움↓  base  panel=밝음↑  elevated↑  light↑↑
// light base: impact=미드톤↓  base  panel≈white  surface=glass
// impact   = darken 40%   (다크: 심도·드라마틱 / 라이트: 포인트 미드톤)
// base     = base          (씬 배경 기준)
// panel    = lighten  8%  (카드·패널, 다크에서 약간 밝음)
// elevated = lighten 16%  (모달·포커스 패널)
// light    = lighten 55%  (전환용 — 다크→라이트 중간)
// surface  = base 78%     (글래스 반투명)
export const mkBg = (b: string) => ({
  impact:   _dim(b, 0.40),
  base:     b,
  panel:    _mix(b, 0.08),
  elevated: _mix(b, 0.16),
  light:    _mix(b, 0.55),
  surface:  _a(b, 0.78),
} as const);

// ─── BG — named 세트 프리셋 ──────────────────────────────────────
// 사용: BG.navy.base  /  BG.dusk.panel  /  BG.slate.impact
// 커스텀: mkBg('#0A1020') 으로 임의 베이스 세트 생성 가능
export const BG = {
  // ── Dark
  navy:        mkBg(BG_PALETTE.navy),
  midnight:    mkBg(BG_PALETTE.midnight),
  deep:        mkBg(BG_PALETTE.deep),
  slate:       mkBg(BG_PALETTE.slate),
  charcoal:    mkBg(BG_PALETTE.charcoal),
  ink:         mkBg(BG_PALETTE.ink),
  dusk:        mkBg(BG_PALETTE.dusk),
  ember:       mkBg(BG_PALETTE.ember),
  umber:       mkBg(BG_PALETTE.umber),
  blueNight:   mkBg(BG_PALETTE.blueNight),
  violetNight: mkBg(BG_PALETTE.violetNight),
  greenNight:  mkBg(BG_PALETTE.greenNight),
  // ── Light
  white:       mkBg(BG_PALETTE.white),
  cloud:       mkBg(BG_PALETTE.cloud),
  pearl:       mkBg(BG_PALETTE.pearl),
  silver:      mkBg(BG_PALETTE.silver),
  linen:       mkBg(BG_PALETTE.linen),
  cream:       mkBg(BG_PALETTE.cream),
  blush:       mkBg(BG_PALETTE.blush),
  iceBlue:     mkBg(BG_PALETTE.iceBlue),
  mintGreen:   mkBg(BG_PALETTE.mintGreen),
  rule: 'rgba(255,255,255,0.07)',
} as const;

// ─── CARD_BG 카드 배경 rgba 프리셋 ────────────────────────────
// backdropFilter: 'blur(8px)' 와 함께 사용. 카드 배경 rgba 통일용
// 사용: <div style={{ background: CARD_BG.base, backdropFilter: 'blur(8px)' }}>
export const CARD_BG = {
  base:    'rgba(10,15,26,0.72)',    // 기본 카드 — 어두운 배경 위 반투명 패널
  danger:  'rgba(239,68,68,0.07)',   // 위험 하이라이트 tint — 레드 강조 카드
  warning: 'rgba(250,204,21,0.07)',  // 경고 하이라이트 tint — 옐로 강조 카드
  surface: 'rgba(248,250,252,0.07)', // 밝은 서피스 tint — 중립 강조 카드
} as const;

// ─── Semantic color — Overlay (MGD §6-1) ──────────────────────
export const OVERLAY = {
  scrimDark:        'rgba(0,0,0,0.70)',
  scrimLight:       'rgba(255,255,255,0.85)',
  gradientBottom:   'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0) 15%, rgba(0,0,0,0.90) 100%)',
  scrimBottom:      'linear-gradient(to top, rgba(6,10,18,0.96) 0%, rgba(6,10,18,0.60) 40%, rgba(6,10,18,0) 68%)',
  scrimLeft:        'linear-gradient(to right, rgba(6,10,18,0.96) 0%, rgba(6,10,18,0.55) 45%, rgba(6,10,18,0) 72%)',
  scrimRight:       'linear-gradient(to left, rgba(6,10,18,0.96) 0%, rgba(6,10,18,0.55) 45%, rgba(6,10,18,0) 72%)',
  scrimFlat:        'rgba(6,10,18,0.58)',
} as const;

// ─── overlayRegion → OVERLAY 직접 매핑 (VM 씬) ────────────────
// storyboard infographic.overlayRegion 문자열을 키로 그대로 lookup
// 사용: background: OVERLAY_BY_REGION[infographic.overlayRegion]
export const OVERLAY_BY_REGION = {
  '화면 하단':       OVERLAY.scrimBottom,
  '화면 좌측':       OVERLAY.scrimLeft,
  '화면 우측':       OVERLAY.scrimRight,
  '전체 화면 중앙':  OVERLAY.scrimFlat,
} as const;

// ─── Progression palettes (designSystem.progression → bg 색조) ──
export const PROGRESSION = {
  dark_sustained: {
    bg:      '#0C1220',
    bgGrad:  'radial-gradient(ellipse 130% 110% at 50% 38%, #162032 0%, #0C1220 68%)',
    panel:   '#1E293B',
    border:  'rgba(255,255,255,0.08)',
  },
  dark_to_light: {
    dark: {
      bg:     '#0C1220',
      bgGrad: 'radial-gradient(ellipse 130% 110% at 50% 38%, #162032 0%, #0C1220 68%)',
      panel:  '#1E293B',
      border: 'rgba(255,255,255,0.08)',
    },
    light: {
      bg:     '#F1F5F9',
      bgGrad: 'radial-gradient(ellipse 130% 110% at 50% 38%, #FFFFFF 0%, #E2E8F0 68%)',
      panel:  '#FFFFFF',
      border: 'rgba(0,0,0,0.08)',
    },
  },
  warm_throughout: {
    bg:      '#1A1208',
    bgGrad:  'radial-gradient(ellipse 130% 110% at 50% 38%, #2a1e0e 0%, #1A1208 68%)',
    panel:   '#2A1E0E',
    border:  'rgba(245,158,11,0.15)',
  },
  light_to_saturated: {
    bg:      '#F8FAFC',
    bgGrad:  'radial-gradient(ellipse 130% 110% at 50% 38%, #FFFFFF 0%, #F1F5F9 68%)',
    panel:   '#FFFFFF',
    border:  'rgba(0,0,0,0.08)',
  },
  light_sustained: {
    bg:      '#F1F5F9',
    bgGrad:  'radial-gradient(ellipse 130% 110% at 50% 38%, #FFFFFF 0%, #E2E8F0 68%)',
    panel:   '#FFFFFF',
    border:  'rgba(0,0,0,0.07)',
  },
  light_to_dark: {
    light: {
      bg:     '#F1F5F9',
      bgGrad: 'radial-gradient(ellipse 130% 110% at 50% 38%, #FFFFFF 0%, #E2E8F0 68%)',
      panel:  '#FFFFFF',
      border: 'rgba(0,0,0,0.07)',
    },
    dark: {
      bg:     '#0C1220',
      bgGrad: 'radial-gradient(ellipse 130% 110% at 50% 38%, #162032 0%, #0C1220 68%)',
      panel:  '#1E293B',
      border: 'rgba(255,255,255,0.08)',
    },
  },
  gradient_shift: {
    bg:      '#0C1220',
    bgGrad:  'radial-gradient(ellipse 130% 110% at 50% 38%, #1e1230 0%, #0C1220 68%)',
    panel:   '#1E1230',
    border:  'rgba(255,255,255,0.08)',
  },
} as const;

// ─── Accent palettes (designSystem.accent → primary 색조) ─────
// onDark: 어두운 배경용 밝은 색조 (400 계열)
// onLight: 밝은 배경용 어두운 색조 (700 계열)
// 직접 접근: ACCENT.amber.onDark.accent
// 페르소나 경유: resolvePersonaColors()가 배경에 따라 자동 선택 → C.accent 그대로 사용
export const ACCENT = {
  // ── Cool
  blue: {
    onDark:  { accent: '#60A5FA', accentGlow: 'rgba(96,165,250,0.28)',  accentDim: 'rgba(96,165,250,0.18)' },
    onLight: { accent: '#1D4ED8', accentGlow: 'rgba(29,78,216,0.18)',   accentDim: 'rgba(29,78,216,0.10)' },
  },
  sky: {
    onDark:  { accent: '#38BDF8', accentGlow: 'rgba(56,189,248,0.26)',  accentDim: 'rgba(56,189,248,0.14)' },
    onLight: { accent: '#0369A1', accentGlow: 'rgba(3,105,161,0.18)',   accentDim: 'rgba(3,105,161,0.10)' },
  },
  cyan: {
    onDark:  { accent: '#22D3EE', accentGlow: 'rgba(34,211,238,0.26)',  accentDim: 'rgba(34,211,238,0.14)' },
    onLight: { accent: '#0E7490', accentGlow: 'rgba(14,116,144,0.18)',  accentDim: 'rgba(14,116,144,0.10)' },
  },
  violet: {
    onDark:  { accent: '#A78BFA', accentGlow: 'rgba(167,139,250,0.26)', accentDim: 'rgba(167,139,250,0.16)' },
    onLight: { accent: '#6D28D9', accentGlow: 'rgba(109,40,217,0.18)',  accentDim: 'rgba(109,40,217,0.10)' },
  },
  // ── Warm
  red: {
    onDark:  { accent: '#F87171', accentGlow: 'rgba(248,113,113,0.26)', accentDim: 'rgba(248,113,113,0.18)' },
    onLight: { accent: '#B91C1C', accentGlow: 'rgba(185,28,28,0.18)',   accentDim: 'rgba(185,28,28,0.10)' },
  },
  deep_red: {
    onDark:  { accent: '#EF4444', accentGlow: 'rgba(239,68,68,0.30)',   accentDim: 'rgba(239,68,68,0.22)' },
    onLight: { accent: '#991B1B', accentGlow: 'rgba(153,27,27,0.20)',   accentDim: 'rgba(153,27,27,0.12)' },
  },
  orange: {
    onDark:  { accent: '#FB923C', accentGlow: 'rgba(251,146,60,0.24)',  accentDim: 'rgba(251,146,60,0.18)' },
    onLight: { accent: '#C2410C', accentGlow: 'rgba(194,65,12,0.18)',   accentDim: 'rgba(194,65,12,0.10)' },
  },
  amber: {
    onDark:  { accent: '#FBBF24', accentGlow: 'rgba(251,191,36,0.28)',  accentDim: 'rgba(251,191,36,0.15)' },
    onLight: { accent: '#B45309', accentGlow: 'rgba(180,83,9,0.18)',    accentDim: 'rgba(180,83,9,0.10)'  },
  },
  yellow: {
    onDark:  { accent: '#FACC15', accentGlow: 'rgba(250,204,21,0.28)',  accentDim: 'rgba(250,204,21,0.12)' },
    onLight: { accent: '#A16207', accentGlow: 'rgba(161,98,7,0.18)',    accentDim: 'rgba(161,98,7,0.10)'  },
  },
  // ── Natural
  green: {
    onDark:  { accent: '#4ADE80', accentGlow: 'rgba(74,222,128,0.28)',  accentDim: 'rgba(74,222,128,0.14)' },
    onLight: { accent: '#15803D', accentGlow: 'rgba(21,128,61,0.18)',   accentDim: 'rgba(21,128,61,0.10)'  },
  },
} as const;

// ─── ACCENT_BG — 액센트 계열 tint 배경 ──────────────────────────
// 기존 ACCENT 컬러와 1:1 대응. 환기·포인트 씬 배경, 카드 강조 tint 용
// tint  : 10% 단색 (카드 bg, 섹션 배경)
// scrim : 상단 집중 radial glow (씬 전면 강조, 인트로 등)
// 사용: background: ACCENT_BG.blue.tint  /  ACCENT_BG.violet.scrim
export const ACCENT_BG = {
  blue:   { tint:'rgba(37,99,235,0.10)',  scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(37,99,235,0.22) 0%, transparent 70%)' },
  sky:    { tint:'rgba(56,189,248,0.10)', scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(56,189,248,0.20) 0%, transparent 70%)' },
  cyan:   { tint:'rgba(6,182,212,0.10)',  scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(6,182,212,0.20) 0%, transparent 70%)' },
  violet: { tint:'rgba(139,92,246,0.10)', scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(139,92,246,0.22) 0%, transparent 70%)' },
  red:    { tint:'rgba(239,68,68,0.10)',  scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(239,68,68,0.22) 0%, transparent 70%)' },
  orange: { tint:'rgba(249,115,22,0.10)', scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(249,115,22,0.20) 0%, transparent 70%)' },
  amber:  { tint:'rgba(245,158,11,0.10)', scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(245,158,11,0.20) 0%, transparent 70%)' },
  green:  { tint:'rgba(34,197,94,0.10)',  scrim:'radial-gradient(ellipse 100% 80% at 50% 0%, rgba(34,197,94,0.20) 0%, transparent 70%)' },
} as const;

// ─── Climax palettes (designSystem.climax → A★ 씬 전용 컬러) ──
// 색 변화를 수반하는 climax만 포함. flash_impact·bloom_spread는 모션 전용.
export const CLIMAX = {
  color_warmth: {
    accent:    '#F59E0B',
    accentGlow:'rgba(245,158,11,0.35)',
  },
  dark_light_flip: {
    bg:   '#F1F5F9',
    text: '#0C1220',
  },
  saturation_peak: {
    // accent는 ACCENT에서 상속, 채도만 최대로 올림
    filterSaturate: 1.4,
  },
} as const;

// ─── Layout ───────────────────────────────────────────────────
// 기준 해상도. ★안전 영역은 여기 두지 않는다★ — 정본은 `@/lib/safe-zone`(데드존 가이드 실측 · 2026-09-23).
//   종전 이 자리의 `safe`(9:16 상 7.81% · 하 10.42% · 좌우 3.70%)는 실제로 잘리는 자리보다 넓어서
//   글자가 플랫폼 버튼·설명글에 먹혔다. 두 벌을 두면 다시 갈라지므로 옛 값을 지우고 읽는 쪽을 정본으로 옮겼다
//   (장표 `useSafeArea` · 가로 자막 띠 `CaptionBand`).
export const LAYOUT = {
  '16:9': { W: 1920, H: 1080 },
  '9:16': { W: 1080, H: 1920 },
  '4:5':  { W: 1080, H: 1350 },
} as const;

// ──────────────────────────────────────────────────────────────
// ─── Component Presets (shadcn-style variant tokens) ──────────
// ──────────────────────────────────────────────────────────────
// 컴포지션 단위 디자인 다양성 확보용. MSB 에이전트가 키 선택, 코드 에이전트는 lookup 후 inline style 적용
// 키 체계·사용 가이드 → MASTER_STORYBOARD_FRAMEWORK_V2.md §3-6, §10-1, §11-3

type AccentColors = { accent: string; accentDim: string; accentGlow?: string; isDark?: boolean };

// ─── CARD 스타일 프리셋 ───────────────────────────────────────
// 사용: <div style={CARD('elevated', C)}>
// filled:   중립 배경 + 흰 테두리 — M-type·정보 나열
// outlined: 중립 배경 + accent 테두리 — accent 계열과 어우러지는 보조 카드
// elevated: 중립 배경 + accent 상단 바 — 강조·섹션 헤더
// ghost:    배경 없음 — 영상 위 오버레이
// accent:   accent 배경 + 테두리 — 경고·핵심 강조
// tinted:   accent 색이 아주 연하게 깔림 — accent보다 약한 soft 강조
// ruled:    왼쪽 accent 바 + 중립 배경 — 인용·설명·위계 구분
export const CARD = (
  variant: 'filled' | 'outlined' | 'elevated' | 'ghost' | 'accent' | 'tinted' | 'ruled',
  C: AccentColors,
) => {
  const d = C.isDark !== false;
  switch (variant) {
    case 'filled':
      return d
        ? { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '24px 28px' }
        : { background: 'rgba(0,0,0,0.04)',       border: '1px solid rgba(0,0,0,0.10)',       borderRadius: 14, padding: '24px 28px' };
    case 'outlined':
      return d
        ? { background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.accentGlow ?? C.accentDim}`, borderRadius: 14, padding: '24px 28px' }
        : { background: 'rgba(0,0,0,0.02)',       border: `1px solid ${C.accentGlow ?? C.accentDim}`, borderRadius: 14, padding: '24px 28px' };
    case 'elevated':
      return d
        ? { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.12)', borderTop: `3px solid ${C.accent}`, borderRadius: 14, padding: '24px 28px' }
        : { background: 'rgba(0,0,0,0.04)',       border: '1px solid rgba(0,0,0,0.08)',       borderTop: `3px solid ${C.accent}`, borderRadius: 14, padding: '24px 28px' };
    case 'ghost':
      return { background: 'transparent', padding: '24px 28px' };
    case 'accent':
      return { background: C.accentDim, border: `1px solid ${C.accent}`, borderRadius: 14, padding: '24px 28px' };
    case 'tinted':
      return { background: C.accentDim, border: `1px solid ${C.accentGlow ?? C.accentDim}`, borderRadius: 14, padding: '24px 28px' };
    case 'ruled':
      return d
        ? { background: 'rgba(255,255,255,0.05)', borderLeft: `4px solid ${C.accent}`, borderRadius: '0 8px 8px 0', padding: '20px 24px' }
        : { background: 'rgba(0,0,0,0.03)',       borderLeft: `4px solid ${C.accent}`, borderRadius: '0 8px 8px 0', padding: '20px 24px' };
  }
};

// ─── BADGE 스타일 프리셋 ──────────────────────────────────────
// 사용: <div style={BADGE('solid', C)}>
// solid:   accent 채움 — 가장 강한 강조
// outlined: 테두리만 — 보조 항목
// ghost:   dim 배경 + 테두리 — 중간 강조
// pill:    solid 캡슐형 — 태그·상태 표시에 부드러운 느낌
// subtle:  배경 아주 연함, 테두리 없음 — 가장 약한 soft 라벨
// 한 카드 내 mixing 허용 — 강조 항목 1개 solid/pill, 나머지 outlined/subtle로 위계 표현
export const BADGE = (
  variant: 'solid' | 'outlined' | 'ghost' | 'pill' | 'subtle',
  C: AccentColors,
) => {
  const base = { padding: '6px 16px', borderRadius: 6, fontWeight: 700, fontFamily: "'Noto Sans KR', Pretendard, sans-serif", display: 'inline-flex', alignItems: 'center' as const };
  switch (variant) {
    case 'solid':
      return { ...base, background: C.accent, color: '#FFFFFF', border: 'none' };
    case 'outlined':
      return { ...base, background: 'transparent', color: C.accent, border: `2px solid ${C.accent}` };
    case 'ghost':
      return { ...base, background: C.accentDim, color: C.accent, border: `2px solid ${C.accent}` };
    case 'pill':
      return { ...base, borderRadius: 9999, padding: '6px 20px', background: C.accent, color: '#FFFFFF', border: 'none' };
    case 'subtle':
      return { ...base, background: C.accentDim, color: C.accent, border: 'none' };
  }
};

// ─── DIVIDER 스타일 프리셋 ────────────────────────────────────
// 카드 내부 항목 구획용.
// 하위 호환: DIVIDER.rule (dark default)
// 표면 인식: DIVIDER.onDark.rule / DIVIDER.onLight.rule
export const DIVIDER = {
  rule:     { height: 1, background: 'rgba(255,255,255,0.08)' },
  dashed:   { height: 0, borderTop: '1px dashed rgba(255,255,255,0.16)' },
  gradient: { height: 1, background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.12), transparent)' },
  onDark: {
    rule:     { height: 1, background: 'rgba(255,255,255,0.08)' },
    dashed:   { height: 0, borderTop: '1px dashed rgba(255,255,255,0.16)' },
    gradient: { height: 1, background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.12), transparent)' },
  },
  onLight: {
    rule:     { height: 1, background: 'rgba(0,0,0,0.10)' },
    dashed:   { height: 0, borderTop: '1px dashed rgba(0,0,0,0.16)' },
    gradient: { height: 1, background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.10), transparent)' },
  },
} as const;

// ─── BG_DENSITY 배경 밀도 레이어 ─────────────────────────────
// atmosphere.bgDensity 키 → CSS 명세 매핑. 다중 키 사용 시 backgroundImage·backgroundSize 조합
// 사용: <AbsoluteFill style={BG_DENSITY['dot-texture']} />
export const BG_DENSITY = {
  'flat':         { backgroundImage: 'none' },
  'dual-glow':    { backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.06) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(255,255,255,0.04) 0%, transparent 50%)' },
  'dot-texture':  { backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px' },
  'grid':         { backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px' },
  'soft-vignette':{ background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.30) 100%)' },
} as const;

// ─── GLOW 효과 프리셋 ─────────────────────────────────────────
// 4-layer boxShadow — tight core → bloom → diffusion → ambient 순으로 자연스러운 빛 감쇠
// intensity: 0→1 페이드인 값 (Remotion interpolate 결과 전달). 기본 1.0
// 사용: <div style={GLOW('accent', C, glowIntensity)}>
// 코드 에이전트 패턴:
//   const glowIntensity = interpolate(frame, [enterAt, enterAt + 20], [0, 1], { extrapolateRight: 'clamp' });
export const GLOW = (
  variant: 'warm' | 'cool' | 'accent' | 'success' | 'danger',
  C?: AccentColors,
  intensity = 1,
) => {
  const k = Math.max(0, Math.min(1, intensity));
  // spread 방식 — 요소 외곽 균등 팽창 후 블러 → 정원형 방사 효과 (채도 낮은 컬러에 적합)
  const s = (r: number, g: number, b: number, a1: number, a2: number, a3: number, a4: number) =>
    `0 0 20px 6px rgba(${r},${g},${b},${+(a1*k).toFixed(3)}), 0 0 60px 16px rgba(${r},${g},${b},${+(a2*k).toFixed(3)}), 0 0 120px 32px rgba(${r},${g},${b},${+(a3*k).toFixed(3)}), 0 0 200px 60px rgba(${r},${g},${b},${+(a4*k).toFixed(3)})`;
  // blur-only 방식 — spread 없이 자연 감쇠. amber처럼 고채도 warm 컬러에 사용 (착색 후광 방지)
  const b_ = (r: number, g: number, b: number, a1: number, a2: number, a3: number, a4: number) =>
    `0 0 16px rgba(${r},${g},${b},${+(a1*k).toFixed(3)}), 0 0 40px rgba(${r},${g},${b},${+(a2*k).toFixed(3)}), 0 0 80px rgba(${r},${g},${b},${+(a3*k).toFixed(3)}), 0 0 140px rgba(${r},${g},${b},${+(a4*k).toFixed(3)})`;
  switch (variant) {
    case 'warm':
      return { boxShadow: b_(245, 158, 11, 0.45, 0.22, 0.10, 0.04) };
    case 'cool':
      return { boxShadow: s(96,  165, 250, 0.50, 0.25, 0.11, 0.04) };
    case 'success':
      return { boxShadow: s(34,  197, 94,  0.50, 0.26, 0.12, 0.04) };
    case 'danger':
      return { boxShadow: s(239, 68,  68,  0.60, 0.32, 0.16, 0.06) };
    case 'accent': {
      const hex = C?.accent ?? '#FFFFFF';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { boxShadow: s(r, g, b, 0.55, 0.28, 0.14, 0.05) };
    }
  }
};

// ─── GLOW_TEXT 텍스트 glow 프리셋 ────────────────────────────
// textShadow 반환 — 반드시 <span> 등 텍스트 요소에 직접 적용. div·wrapper 금지
// neutral: 흰색 기반 — 어떤 텍스트 컬러에도 무난하게 어울리는 범용 글로우
// 사용: <span style={{ ...GLOW_TEXT('neutral', undefined, glowIntensity) }}>텍스트</span>
export const GLOW_TEXT = (
  variant: 'warm' | 'cool' | 'accent' | 'success' | 'danger' | 'neutral',
  C?: AccentColors,
  intensity = 1,
) => {
  const k = Math.max(0, Math.min(1, intensity));
  // tight(8px) → bloom(28px) → diffuse(56px) 3-layer. 값이 높으면 글자 가독성 저하 주의
  const s = (r: number, g: number, b: number, a1: number, a2: number, a3: number) =>
    `0 0 8px rgba(${r},${g},${b},${+(a1*k).toFixed(3)}), 0 0 28px rgba(${r},${g},${b},${+(a2*k).toFixed(3)}), 0 0 56px rgba(${r},${g},${b},${+(a3*k).toFixed(3)})`;
  switch (variant) {
    case 'neutral':
      return { textShadow: s(255, 255, 255, 0.18, 0.09, 0.04) }; // 범용 — 어떤 색상에도 사용 가능
    case 'warm':
      return { textShadow: s(245, 158, 11, 0.38, 0.20, 0.08) };
    case 'cool':
      return { textShadow: s(96,  165, 250, 0.34, 0.17, 0.07) };
    case 'success':
      return { textShadow: s(34,  197, 94,  0.34, 0.17, 0.07) };
    case 'danger':
      return { textShadow: s(239, 68,  68,  0.38, 0.20, 0.08) };
    case 'accent': {
      const hex = C?.accent ?? '#FFFFFF';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { textShadow: s(r, g, b, 0.38, 0.20, 0.08) };
    }
  }
};

// ─── SHADOW 그림자 프리셋 ─────────────────────────────────────
// 카드 깊이감 표현. 사용: <div style={{ boxShadow: SHADOW.md }}>
export const SHADOW = {
  sm: '0 2px 8px rgba(0,0,0,0.20)',
  md: '0 4px 16px rgba(0,0,0,0.30)',
  lg: '0 8px 32px rgba(0,0,0,0.40)',
  xl: '0 16px 48px rgba(0,0,0,0.50)',
} as const;

// ─── TRANSITION 프리셋 ────────────────────────────────────────
// 씬·요소 진입·전환 효과 파라미터. 코드 에이전트가 Remotion interpolate로 변환
// 사용: TRANSITION.fade.duration → frame 수
export const TRANSITION = {
  // ── Fade
  fadeFast:   { type: 'fade',  duration: 15 },                              // 0.5s @ 30fps
  fade:       { type: 'fade',  duration: 30 },                              // 1s
  fadeSlow:   { type: 'fade',  duration: 60 },                              // 2s
  // ── Slide
  slideUp:    { type: 'slide', duration: 24, dir: 'up',    offset: 40 },   // 아래→위
  slideDown:  { type: 'slide', duration: 24, dir: 'down',  offset: 40 },   // 위→아래 (헤더·오버레이)
  slideRight: { type: 'slide', duration: 24, dir: 'right', offset: 40 },   // 왼쪽→오른쪽
  slideLeft:  { type: 'slide', duration: 24, dir: 'left',  offset: 40 },   // 오른쪽→왼쪽
  // ── Scale
  pop:        { type: 'pop',   duration: 18, scaleFrom: 0.85 },            // 살짝 작게→1
  popIn:      { type: 'pop',   duration: 20, scaleFrom: 0.0 },             // 0에서 팝업 (아이콘·배지)
  zoomIn:     { type: 'zoom',  duration: 22, scaleFrom: 1.12 },            // 살짝 크게→1 (카메라 당기기)
  // ── Special
  blur:       { type: 'blur',  duration: 24, blurPx: 10 },                 // 블러 디졸브 (filter blur → 0)
  wipe:       { type: 'wipe',  duration: 30 },                             // scaleX 0→1 좌→우
} as const;

// ──────────────────────────────────────────────────────────────

// ─── Idle Motion (체류 구간 미세 루프) ────────────────────────
// 씬 entrance 완료 후 체류 구간 적용. MSB 에이전트는 키 1개만 선택 (atmosphere.idleMotion)
// 코드 에이전트는 IDLE_MOTION[key] lookup 후 Remotion interpolate로 변환
// 키 체계·권장 매핑 → MASTER_STORYBOARD_FRAMEWORK_V2.md §9-2
export const IDLE_MOTION = {
  static:  null,                                                       // 무동작 — A/B/C 기본
  pulse:   { type: 'opacity',    range: [0.96, 1.0],  periodSec: 3 },  // A★ 일반
  breathe: { type: 'scale',      range: [0.97, 1.03], periodSec: 4 },  // A★ bloom_spread
  shimmer: { type: 'brightness', range: [1.0,  1.12], periodSec: 2 },  // A★ color_warmth
} as const;

// ─── Watermark ────────────────────────────────────────────────
// AI 제작 표시 위치·투명도. 사용: right: Math.round(W * AI_WATERMARK.right)
export const AI_WATERMARK = {
  text:       '이 영상은 AI로 제작되었습니다',  // 고정 문자열 — 임의 변경 금지
  right:      60/1920,               // → 60px @ FHD / 120px @ 4K
  top:        40/1080,               // → 40px @ FHD /  80px @ 4K
  opacity:    0.6,
  fontFamily:  'Pretendard, sans-serif',
  fontSize:    T.label,              // 30px — default.ts T.label 기준 고정
  fontWeight:  600,                  // SemiBold — Pretendard 지원, fallback → 700 근사
} as const;

// ─── EASING 커브 프리셋 ────────────────────────────────
// Remotion interpolate easing 옵션에 직접 전달
// 사용: interpolate(frame, [0, 24], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: EASING.snap })
// ⚠️ 한쪽 clamp만 적용 금지 — 베지어 외삽으로 깜빡임 발생 (값이 [0,1] 밖으로 튐)
export const EASING = {
  linear:    Easing.linear,
  easeOut:   Easing.bezier(0,    0,    0.58, 1   ),  // CSS ease-out — 기본 감속 (기본 추천)
  easeIn:    Easing.bezier(0.42, 0,    1,    1   ),  // CSS ease-in  — 기본 가속
  easeInOut: Easing.bezier(0.42, 0,    0.58, 1   ),  // S커브 — 부드러운 대칭 전환
  snap:      Easing.bezier(0.2,  0,    0,    1   ),  // 빠르게 치고 부드럽게 안착 (slideUp·infographic 추천)
  spring:    Easing.bezier(0.34, 1.56, 0.64, 1   ),  // 살짝 튕김 — A★ 강조 요소용
} as const;

// ─── STAGGER 순차 진입 딜레이 ─────────────────────────
// 리스트·카드 순차 진입 시 enterAt 오프셋. @ 30fps
// 사용: const enterAt = baseFrame + index * STAGGER.normal;
export const STAGGER = {
  normal: 10,  // 0.33s — 기본 (3-4개)
  loose:  16,  // 0.53s — 여유로운 강조 (2-3개)
  slow:   22,  // 0.73s — 매우 강조된 순차 (2개)
} as const;

// ─── SPRING 물리 파라미터 ──────────────────────────────
// Remotion spring() 의 config 객체. EASING과 짝을 이루는 spring 기반 애니메이션용
// 사용: spring({ frame, fps, config: SPRING.reveal, durationInFrames })
export const SPRING = {
  snap:      { damping: 200, stiffness: 260 }, // 튕김 없이 즉각 안착 — 배지·태그·작은 UI 진입
  reveal:    { damping: 26,  stiffness: 190 }, // 타이틀 마스크 리빌 — useMaskReveal 기본값
  bounce:    { damping: 28,  stiffness: 180 }, // 약한 오버슛 — 강조 요소 진입 (A★ 씬)
  slam:      { damping: 12,  stiffness: 420 }, // 강한 충격+큰 바운스 — 배지 드롭·임팩트 강조
  softSlide: { damping: 32,  stiffness: 180 }, // 부드러운 슬라이드업 — 정보 카드·보조 요소
  fastSnap:  { damping: 200, stiffness: 280 }, // 빠른 안착 (snap보다 살짝 강함) — 카드·섹션 진입
} as const;

// ─── BG_TEXTURE 배경 텍스처 파라미터 ──────────────────
// BackgroundTexture 컴포넌트의 기본값 집합. 씬 직접 구현 시에도 이 값을 참조
// 사용: <BackgroundTexture /> 기본값과 동일. 오버라이드: <BackgroundTexture dotOpacity={BG_TEXTURE.dot.opacity} />
export const BG_TEXTURE = {
  dot:       { opacity: 0.038, sizePx: 52 },           // 도트 그리드 — 미세 텍스처감
  scanlines: { lineOpacity: 0.04, linePx: 3, gapPx: 1 }, // 수평 스캔라인 — 영상 질감
  vignette:  { strength: 0.78, startPct: 35 },         // 주변부 비네트 — 시선 중앙 집중
} as const;

// ─── FLASH_CUT 씬 진입 플래시 타이밍 ─────────────────
// useFlashCut() 의 내부 키프레임. 직접 interpolate 작성 시 참조
// 사용: interpolate(frame, FLASH_CUT.flashFrames, [1, 1, 0], { extrapolateRight: 'clamp' })
export const FLASH_CUT = {
  flashFrames: [0, 5, 22] as const, // [시작, 유지 끝, 소멸 끝] — 화이트 플래시
  sceneFrames: [0, 38]    as const, // [시작, 끝] — 씬 전체 페이드인
} as const;

// ─── BLOB_COLOR 블롭 배경 그라디언트 프리셋 ───────────────────
// Blob 컴포넌트의 background 프롭에 직접 전달. 3-stop radial-gradient
// 사용: <Blob background={BLOB_COLOR.red} width={320} blur={120} opacity={op} />
export const BLOB_COLOR = {
  red:   'radial-gradient(circle, rgba(239,68,68,0.44) 0%, rgba(185,28,28,0.14) 45%, transparent 70%)',
  green: 'radial-gradient(circle, rgba(34,197,94,0.36) 0%, rgba(22,163,74,0.10) 45%, transparent 70%)',
  blue:  'radial-gradient(circle, rgba(56,96,210,0.36) 0%, rgba(30,100,220,0.12) 45%, transparent 70%)',
  amber: 'radial-gradient(circle, rgba(245,158,11,0.32) 0%, rgba(180,100,0,0.12) 45%, transparent 70%)',
} as const;

// ─── Animation Pattern defaults (30fps 기준) ──────────────────
// ANIMATION_CLASSIFICATION.md 실구현 패턴의 검증된 기본값
// 에이전트가 값을 지어내지 않고 이 토큰을 참조하여 구현
// underlineSweep/swipeHighlight/boxSliderHighlight: 반드시 텍스트 wrapper로 사용 (자동 너비 매칭)
//   <UnderlineSweep f={f} colors={C}>핵심 텍스트</UnderlineSweep>
//   부동 라인·박스 금지 (texts 없이 단독 사용 불가)
export const PATTERN = {
  underlineSweep: {              // 텍스트 아래 언더라인 좌→우 scaleX 리빌 — accent_line_sweep 후속
    durationFrames: 18,          // 0.6s
    heightPx:        2,          // 언더라인 두께
    gapPx:          12,          // 텍스트 baseline에서 라인까지 간격
  },
  swipeHighlight: {              // 텍스트 배경에 컬러 fill 좌→우 리빌
    durationFrames: 18,          // 0.6s — clipPath inset 리빌 속도
  },
  boxSliderHighlight: {          // 텍스트 배경에 박스 슬라이드 + 텍스트 컬러 플립
    durationFrames: 12,          // 0.4s
    boxOpacity:     0.18,        // 박스 배경 불투명도
    textColorThreshold: 0.5,     // boxProgress 이 값 초과 시 글자색 전환
    borderRadius:   8,           // px
  },
  scaleStamp: {
    startScale:  1.3,            // 착지 전 초기 스케일
    endScale:    1.0,
    spring:      { damping: 14, stiffness: 120, mass: 1.2 },
    afterimage:  { blur: 4, startScale: 1.5, endScale: 1.2 },
    afterimageFrames:  [0, 3, 9, 30] as const,
    afterimageOpacity: [0, 0.20, 0.12, 0] as const,
  },
  particleRise: {
    count:           30,
    sizeMin:          2,         // px
    sizeMax:         20,         // px
    speedMin:        0.3,        // 상승 속도 배수
    speedMax:        1.1,
    fadeInDuration:  15,         // 0.5s
    blurThreshold:   14,         // px 이상이면 blur(1px) DOF
  },
  panoramaSlideshow: {
    holdDuration:      42,
    slideDuration:     15,
    photoStartDelay:   24,
    depthScaleFocus:   1.0,
    depthScaleNeighbor: 0.8,
  },
  // ── 파티클 변형 4종 (ParticleRise 동일 구조, 동작만 다름) ──
  particleFall: {              // 상→하 낙하 (Rise 방향 반전)
    count:          30,
    sizeMin:         2,
    sizeMax:        20,
    speedMin:       0.3,
    speedMax:       1.1,
    fadeInDuration: 15,
    blurThreshold:  14,
  },
  particleFloat: {             // 정지 드리프트 — sin/cos 위치 진동 + opacity 맥동
    count:       24,
    sizeMin:      3,
    sizeMax:     14,
    driftAmp:     5,           // ± % of container per axis
    pulsePeriod: 90,           // frames per opacity cycle
    opacityMin:  0.15,
    opacityMax:  0.75,
  },
  particleBurst: {             // 중심에서 방사형 폭발
    count:         40,
    burstDuration: 50,         // frames to expand
    loopPeriod:   110,
    radiusMin:     80,         // px from center at full burst
    radiusMax:    260,
    sizeMin:        4,
    sizeMax:       14,
  },
  confetti: {                  // 회전하는 직사각형 낙하
    count:      35,
    sizeWMin:    8,            // width px
    sizeWMax:   22,
    sizeHMin:    5,            // height px
    sizeHMax:   14,
    speedMin:   0.4,
    speedMax:   1.2,
    rotSpeedMin: 2,            // deg/frame
    rotSpeedMax: 8,
  },
} as const;

// ─── ATMOSPHERE_A 배경 밀도 컴포넌트 카탈로그 ────────────────
// 씬당 1~3종 선택. 성격 혼합 권장 (정적+동적). 배경 타입 매칭 필수.
// 인접 씬 동일 조합 금지. 상세 스펙 → COMPONENT_CATALOG/ATMOSPHERE.md
export const ATMOSPHERE_A = {
  css_particle_glow: { nature: '부유+호흡', mood: '긴장·경각심·몰입·신비·차분',      bg: 'dark'  as const, density: 'medium-high' as const, note: '글로우+파티클 올인원 · warm_glow/floating_orbs와 중복 금지' },
  warm_glow:         { nature: '호흡',      mood: '따뜻함·평온·안도·정성·돌봄',      bg: 'dark'  as const, density: 'low'         as const, note: '경량 — 반드시 다른 컴포넌트와 조합' },
  floating_orbs:     { nature: '부유',      mood: '잔잔함·실내·평온·서정',            bg: 'dark'  as const, density: 'low-medium'  as const, note: 'warm_glow와 조합 → 다크 기본 조합' },
  warm_bokeh:        { nature: '부유',      mood: '포근·실내조명·따뜻한공간·촛불',    bg: 'dark'  as const, density: 'medium'      as const, note: 'floating_orbs와 동시 사용 비권장' },
  steam_rise:        { nature: '상승',      mood: '온기·희망·성장·조리·정성',         bg: 'dark'  as const, density: 'low-medium'  as const, note: '반드시 warm_glow 등 원경 레이어와 조합' },
  gradient_wave:     { nature: '흐름',      mood: '범용 라이트·밝음·긍정·변화',       bg: 'light' as const, density: 'medium'      as const, note: '라이트 배경 기본 밀도 레이어' },
  light_shift:       { nature: '흐름',      mood: '전환·변화·안정·마무리',            bg: 'light' as const, density: 'low'         as const, note: 'gradient_wave 경량 대안' },
  soft_light_rays:   { nature: '빛줄기',    mood: '여명·희망·전환·따뜻한 오후',       bg: 'light' as const, density: 'low'         as const, note: 'Dark→Light 전환 후 빛 연출' },
  light_leaks:       { nature: '임팩트',    mood: 'A★ 전용 · 임팩트 · 감동 · 전환',  bg: 'both'  as const, density: 'high'        as const, note: 'A★ 씬 전용 · screen blendMode 오버레이' },
  particle_rise:     { nature: '상승',      mood: '축제감·성장·성취·결의·타이틀',     bg: 'both'  as const, density: 'medium-high' as const, note: '정적 화면 밀도 보충 · steam_rise와 중복 비권장' },
  vignette_pulse:    { nature: '맥동',      mood: '긴장·위기·경각심·불안·심장박동',   bg: 'dark'  as const, density: 'medium'      as const, note: 'BPM 가속으로 긴장 고조 · screen 이너 글로우' },
} as const;

// ─── ATMOSPHERE_B 리빌 악센트 카탈로그 ────────────────────────
// 씬 중심 강조 요소 등장 시 후광·악센트. 씬당 최대 1종. 씬 주요 모션 유형에 매칭 필수.
// Typewriter 씬은 생략 가능. 인접 씬 동일 악센트 금지.
// 텍스트 종속 디코(언더라인·하이라이트·박스)는 PATTERN.underlineSweep/swipeHighlight/boxSliderHighlight 사용
// 모션 × 악센트 매칭표 → COMPONENT_CATALOG/ATMOSPHERE.md §0-3
export const ATMOSPHERE_B = {
  glow_pulse:        { motion: 'Pop/Spring · Squeeze · Stagger', priority: 1 as const, desc: '원형 글로우 번쩍→소멸 · 4-stop radial-gradient · 씬 중심' },
  particle_burst:    { motion: 'Slam / Drop',                    priority: 1 as const, desc: '방사형 입자 폭발 · 착지 완료 시점에 트리거 · 씬 중심' },
  ring_ripple:       { motion: 'Pin Drop · Pop',                 priority: 2 as const, desc: '동심원 2~3개 확장 소멸 · 아이콘/포인트 강조 · 씬 중심' },
} as const;

// ─── VFX_CATALOG 후처리 VFX 카탈로그 ──────────────────────────
// 씬당 최대 3종. vfxIntensity 엔벨로프 공통 적용.
// scope: 'vm'=영상 씬 전용 · 'both'=M 씬도 사용 가능
// 상세 스펙 + 구현 코드 → COMPONENT_CATALOG/VFX.md
export const VFX_CATALOG = {
  film_grain:           { intent: '필름 질감 · AI 어색함 보정',       scene: ['위기/긴장', '다큐/리얼리티'] as const, blend: 'overlay' as const, scope: 'both' as const, timing: 'persistent' as const },
  chromatic_aberration: { intent: 'RGB 채널 분리 · 충격 표현',         scene: ['충격/임팩트', '위기/긴장']   as const, blend: 'screen'  as const, scope: 'both' as const, timing: 'climax'     as const },
  horizontal_glitch:    { intent: '수평 디지털 오류 · 불안정/고장',    scene: ['충격/임팩트', '경고/위험']   as const, blend: 'screen'  as const, scope: 'both' as const, timing: 'climax'     as const },
  strobe_flash:         { intent: '고주파 밝기 펄스 · 경고등/비상',    scene: ['경고/위험', '충격/임팩트']   as const, blend: 'screen'  as const, scope: 'both' as const, timing: 'climax'     as const },
  radial_zoom_blur:     { intent: '방사형 충격파 · 폭발/충돌 (1회성)', scene: ['충격/임팩트']                as const, blend: 'none'    as const, scope: 'both' as const, timing: 'climax'     as const },
  video_scale_wiggle:   { intent: '핸드헬드 카메라 떨림 · 현장감',     scene: ['다큐/리얼리티', '위기/긴장'] as const, blend: 'none'    as const, scope: 'vm'   as const, timing: 'persistent' as const },
  // V2.23 추가
  white_flash:          { intent: '전체 백색 플래시 · 진입/전환',      scene: ['진입/전환']                  as const, blend: 'screen'  as const, scope: 'both' as const, timing: 'entry'      as const },
  vhs_distort:          { intent: 'VHS 색번짐+노이즈+트래킹',          scene: ['다큐/리얼리티', '경고/위험'] as const, blend: 'overlay' as const, scope: 'both' as const, timing: 'persistent' as const },
  scan_distort:         { intent: '가로 스캔라인 이동',                scene: ['데이터/분석']                as const, blend: 'screen'  as const, scope: 'both' as const, timing: 'persistent' as const },
} as const;

// VFX timing → 자동 트리거 시점 (PersonaLayer가 사용)
//   'entry'      → 씬 시작 시 발동 (frame 0)
//   'climax'     → 씬 중간 발동 (duration / 2)
//   'persistent' → 씬 전체 지속 (frame 0부터 끝까지)
export type VfxTiming = 'entry' | 'climax' | 'persistent';

// ─── KO_TEXT 한국어 텍스트 하드룰 ────────────────────────────────
// HARD RULE: 한국어 텍스트가 포함된 모든 div에 반드시 스프레드 적용
// ...KO_TEXT — wordBreak: 'keep-all' 없이 한국어 줄바꿈 금지
// overflowWrap: 'break-word' — URL/긴 영문 overflow 방어
// whiteSpace: 'pre-line' — \n 개행 처리
export const KO_TEXT = {
  wordBreak:    'keep-all'   as const,
  overflowWrap: 'break-word' as const,
  whiteSpace:   'pre-line'   as const,
} as const;

// ──────────────────────────────────────────────────────────────────
// ─── Atmosphere & VFX 컴포넌트 구현체 ─────────────────────────────
// ──────────────────────────────────────────────────────────────────
// ATMOSPHERE_A/B, VFX_CATALOG 메타데이터 토큰의 실제 React 구현체.
// 모두 transparent overlay — 부모 AbsoluteFill의 background 위에 얹힘.
//
// 색상 props 룰 (V2.21): 모든 컴포넌트는 colors prop으로 씬 컬러 받음. 팔레트 하드코딩 금지.
//   - 그라데이션 카테고리(WarmGlow·GradientWave·LightShift·LightLeaks·VignettePulse): colors.accent 저알파 강제 (씬 톤온톤)
//   - 파티클 카테고리(CssParticleGlow·FloatingOrbs·WarmBokeh·SteamRise·SoftLightRays·ParticleRise): scheme prop으로 'base'(colors.panel) 또는 'accent'(colors.accent) 선택
//   - 리빌 악센트(GlowPulse·ParticleBurst·RingRipple): colors.accent / colors.accentDim / colors.accentGlow 사용
//
// z-order 룰 (V2.21):
//   M:  bg → atmosphere bg → 콘텐츠 → atmosphere reveal (VFX 금지)
//   V:  bg → atmosphere bg → video → VFX → AI 워터마크
//   VM: bg → atmosphere bg → video → VFX → scrim → infographic → atmosphere reveal
//
// 사용 패턴:
//   <AbsoluteFill style={{ background: C.bgGrad }}>
//     {atmosphere.bgComponents?.map(id => <AtmosphereLayer key={id} id={id} f={f} colors={C} scheme={atmosphere.colorScheme} />)}
//     <Video />
//     {vfxLayers?.videoOnly?.map(id => <VfxLayer key={id} id={id} f={f} colors={C} />)}
//     {/* scrim, infographic ... */}
//     {f >= revealAt && atmosphere.revealComponents?.map(id => <AtmosphereLayer key={id} id={id} f={f - revealAt} colors={C} />)}
//   </AbsoluteFill>

export type AtmosphereColors = {
  bg:         string;  // 6-digit hex — 씬 base bg
  panel:      string;  // 6-digit hex — bg보다 약간 밝은(다크) / 어두운(라이트) 변형. 톤온톤 베이스
  accent:     string;  // 6-digit hex — 씬 accent (resolvePersonaColors가 surface에 맞게 자동 선택)
  accentDim:  string;  // rgba 또는 hex+alpha — 저강도 accent tint
  accentGlow: string;  // rgba — 글로우용 accent
  isDark?:    boolean; // 배경 명도 플래그 — CARD/BADGE/DIVIDER 분기 및 STATUS 선택에 사용. 미설정 시 dark 취급
  // ── 텍스트 색 (resolvePersonaColors 자동 설정 — base:'light'→mainDark, base:'dark'→mainLight)
  // inline JSX에서 TEXT.mainLight 하드코딩 금지 → 항상 C.textMain / C.textSub 사용
  textMain?:  string;  // 메인 텍스트 색 (밝은 bg → #000, 어두운 bg → #FFF)
  textSub?:   string;  // 보조 텍스트 색 (밝은 bg → subDark, 어두운 bg → subLight)
  // ── STATUS 세트 (resolvePersonaColors 자동 설정 — base:'light'→STATUS_LIGHT, base:'dark'→STATUS)
  // 컴포넌트에서 STATUS.warningText 직접 참조 금지 → 항상 C.status?.warningText 사용
  status?:    typeof STATUS | typeof STATUS_LIGHT;
};

type _SP   = { f: number };
type _SPC  = _SP & { colors: AtmosphereColors };
type _SPCS = _SPC & { scheme?: 'base' | 'accent' };  // 파티클 카테고리 전용

const _SD = 120; // showcase 기준 4s @ 30fps — 절대 frame 기준점
const _sr = (i: number, s = 0) => Math.abs(Math.sin(i * 127.1 + s * 311.7)) % 1;
const _pcol = (c: AtmosphereColors, scheme: 'base' | 'accent' = 'accent') =>
  scheme === 'accent' ? c.accent : c.panel;

// ─── 렌더-세이프 SVG radial 헬퍼 ──────────────────────────────────
// ★CSS radial-gradient(및 backgroundImage 타일)는 @remotion/web-renderer 렌더 캡처에서 안 칠해진다
//   (2026-07-15 렌더 스틸 실측: warm_glow·vignette_pulse 비검정 픽셀 0.00% — 프리뷰 OK≠렌더 OK.
//    linear-gradient shorthand 는 정상). PersonaLayer 배경·딥블루 배경(lecture.tsx)과 동일 근본 →
//   같은 처방: SVG radialGradient 는 같은 하네스에서 렌더 증명됨.
// CSS `radial-gradient(ellipse rx% ry% at cx% cy%, …)` 등가 — cx/cy/rx/ry 는 0~1 비율
//   (objectBoundingBox: r=rx 가 축별 스케일로 폭·높이에 각각 적용 → y 축만 ry/rx 로 스쿼시).
// gradient id 는 전 파라미터 결정론 파생 — 동일 id ⇒ 동일 정의라 동시 마운트 충돌 무해 (PersonaLayer 관용구).
type _SvgStop = { offset: number; color: string; opacity: number };
const SvgRadial: React.FC<{
  cx: number; cy: number; rx: number; ry: number;
  stops: ReadonlyArray<_SvgStop>;
  style?: React.CSSProperties;
}> = ({ cx, cy, rx, ry, stops, style }) => {
  const id = `msb-rg-${[cx, cy, rx, ry].map((v) => v.toFixed(4)).join('-')}-${stops
    .map((s) => `${s.offset}_${s.color}_${s.opacity}`)
    .join('-')}`.replace(/[^a-zA-Z0-9_-]/g, 'x');
  return (
    <svg width="100%" height="100%" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...style }}>
      <defs>
        <radialGradient id={id} cx={cx} cy={cy} r={rx} gradientTransform={`translate(0 ${cy}) scale(1 ${ry / rx}) translate(0 ${-cy})`}>
          {stops.map((s, i) => (
            <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
          ))}
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
};

// ─── ATMOSPHERE A (배경 ambient · 씬당 1-3종) ────────────────────

// A1. css_particle_glow — 부유+호흡 올인원 (파티클 카테고리, scheme prop)
export const CssParticleGlow: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const col = _pcol(colors, scheme);
  const gOp1 = interpolate(Math.sin(f * 0.025), [-1, 1], [0.03, 0.08]);
  const gOp2 = interpolate(Math.sin(f * 0.018 + 1), [-1, 1], [0.02, 0.06]);
  const g1x = Math.sin(f * 0.004) * 80, g1y = Math.cos(f * 0.003) * 60;
  const g2x = Math.cos(f * 0.005) * 100, g2y = Math.sin(f * 0.004) * 70;
  const pts = Array.from({ length: 48 }, (_, i) => {
    const spd = 0.018 + _sr(i, 3) * 0.007;
    const ph = _sr(i, 4) * Math.PI * 2;
    const ph2 = _sr(i, 5) * Math.PI * 2;
    const isLg = i < 9, isMd = i >= 9 && i < 27;
    const range = isLg ? 170 : isMd ? 110 : 65;
    const x = _sr(i, 1) * 1920 + Math.sin(f * spd + ph) * range * 0.55 + Math.sin(f * spd * 1.4 + ph2) * range * 0.45;
    const y = _sr(i, 2) * 1080 + Math.cos(f * spd * 0.85 + ph) * range * 0.5 + Math.cos(f * spd * 1.2 + ph2) * range * 0.5;
    const sz = isLg ? 14 + _sr(i, 6) * 18 : isMd ? 5 + _sr(i, 6) * 9 : 2 + _sr(i, 6) * 5;
    const op = (isLg ? 0.25 : isMd ? 0.15 : 0.10) + _sr(i, 7) * (isLg ? 0.35 : 0.30) * (0.2 + 0.8 * Math.abs(Math.sin(f * 0.022 + ph)));
    const sc = isLg ? 0.7 + Math.abs(Math.sin(f * 0.03 + ph)) * 0.6 : 1;
    const t = _sr(i, 8) < 0.65 ? 'c' : _sr(i, 8) < 0.85 ? 'd' : 'r';
    return { x, y, sz, op, sc, t, isLg };
  });
  // ★전체를 단일 SVG 로 구현 (2026-07-16 렌더 스틸 실측): 이 컴포넌트의 구 div 파티클+CSS radial
  //   블롭 조합은 web-renderer 렌더에서 통째로 안 칠해졌다 (비검정 0.00% — 프리뷰 OK≠렌더 OK.
  //   등가 재구성 트리는 칠해지는데 실물만 죽는 페인터 기벽이라 원인 특정 불가) → SVG 프리미티브는
  //   같은 하네스에서 전 케이스 렌더 증명이라 안전 지대로 전면 이관. 기하·알파·모션 수치는 구 CSS 그대로
  //   (블롭 farthest-corner≈r0.7 · 파티클 circle/diamond(rx2)/ring · isLg=blur2px+글로우 halo).
  const uid = `msb-cpg-${col.replace(/[^a-zA-Z0-9]/g, '')}`; // 색 결정론 id — 동색 동시 마운트 충돌 무해
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <svg width="100%" height="100%" viewBox="0 0 1920 1080" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <radialGradient id={`${uid}-b`} cx={0.5} cy={0.5} r={0.7}>
            <stop offset={0} stopColor={col} stopOpacity={1} />
            <stop offset={0.7} stopColor={col} stopOpacity={0} />
          </radialGradient>
          <radialGradient id={`${uid}-h`} cx={0.5} cy={0.5} r={0.5}>
            <stop offset={0} stopColor={col} stopOpacity={0x60 / 255} />
            <stop offset={1} stopColor={col} stopOpacity={0} />
          </radialGradient>
          <filter id={`${uid}-blur`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={2} />
          </filter>
        </defs>
        {/* 글로우 블롭 2종 — 코어 알파(col44/col33)는 element opacity 로 합산 */}
        <rect x={-100 + g1x} y={-100 + g1y} width={800} height={600} fill={`url(#${uid}-b)`} opacity={gOp1 * (0x44 / 255)} />
        <rect x={1300 - g2x} y={660 - g2y} width={700} height={500} fill={`url(#${uid}-b)`} opacity={gOp2 * (0x33 / 255)} />
        {pts.map((p, i) => (
          <g key={i} opacity={p.op} filter={p.isLg ? `url(#${uid}-blur)` : undefined}>
            {p.isLg ? <circle cx={p.x} cy={p.y} r={p.sz * 1.5 * p.sc} fill={`url(#${uid}-h)`} /> : null}
            {p.t === 'd' ? (
              <rect x={-p.sz / 2} y={-p.sz / 2} width={p.sz} height={p.sz} rx={2} fill={col}
                transform={`translate(${p.x} ${p.y}) scale(${p.sc}) rotate(${45 + f * 0.5 * (_sr(i, 10) - 0.5)})`} />
            ) : p.t === 'r' ? (
              <circle cx={p.x} cy={p.y} r={(p.sz / 2) * p.sc} fill="none" stroke={col} strokeWidth={1} />
            ) : (
              <circle cx={p.x} cy={p.y} r={(p.sz / 2) * p.sc} fill={col} />
            )}
          </g>
        ))}
      </svg>
    </AbsoluteFill>
  );
};

// A2. warm_glow — 호흡 경량 (그라데이션 카테고리 · 씬 톤온톤)
export const WarmGlow: React.FC<_SPC> = ({ f, colors }) => {
  const op1 = interpolate(Math.sin(f * 0.022), [-1, 1], [0.15, 0.50]);
  const op2 = interpolate(Math.sin(f * 0.018 + 2), [-1, 1], [0.10, 0.40]);
  const op3 = interpolate(Math.sin(f * 0.015 + 1), [-1, 1], [0.08, 0.30]);
  const x1 = 30 + Math.sin(f * 0.003) * 5, y1 = 35 + Math.cos(f * 0.004) * 5;
  const x2 = 70 + Math.cos(f * 0.005) * 6, y2 = 65 + Math.sin(f * 0.003) * 5;
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <SvgRadial cx={x1 / 100} cy={y1 / 100} rx={0.60} ry={0.55}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x25 / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]}
        style={{ opacity: op1 }} />
      <SvgRadial cx={x2 / 100} cy={y2 / 100} rx={0.55} ry={0.50}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x1a / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]}
        style={{ opacity: op2 }} />
      <SvgRadial cx={0.5} cy={0.5} rx={0.40} ry={0.35}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x14 / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]}
        style={{ opacity: op3 }} />
    </AbsoluteFill>
  );
};

// A3. floating_orbs — 부유 (파티클 카테고리, scheme prop)
export const FloatingOrbs: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const col = _pcol(colors, scheme);
  const orbs = Array.from({ length: 12 }, (_, i) => {
    const spd = 0.01 + _sr(i, 3) * 0.002;
    const ph = _sr(i, 4) * Math.PI * 2;
    const x = _sr(i, 1) * 1800 + 60 + Math.sin(f * spd + ph) * 60 + Math.cos(f * spd * 1.3 + ph) * 20;
    const y = _sr(i, 2) * 900 + 90 + Math.cos(f * spd * 0.8 + ph) * 40 + Math.sin(f * spd * 1.1 + ph) * 15;
    const sz = 8 + _sr(i, 5) * 24;
    const op = (0.05 + _sr(i, 6) * 0.15) * (0.5 + 0.5 * Math.abs(Math.sin(f * 0.015 + ph)));
    return { x, y, sz, op, bl: sz < 16 ? 1 : 3 };
  });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {orbs.map((o, i) => (
        <div key={i} style={{
          position: 'absolute', left: o.x, top: o.y, width: o.sz, height: o.sz,
          borderRadius: '50%', background: col, opacity: o.op,
          transform: 'translate(-50%,-50%)', filter: `blur(${o.bl}px)`,
          boxShadow: o.sz > 20 ? `0 0 ${o.sz}px ${col}40` : 'none',
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A4. warm_bokeh — 부유 큰 흐릿원 (파티클 카테고리, scheme prop)
export const WarmBokeh: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const col = _pcol(colors, scheme);
  const bokeh = Array.from({ length: 8 }, (_, i) => {
    const spd = 0.005 + _sr(i, 3) * 0.001;
    const ph = _sr(i, 4) * Math.PI * 2;
    const x = _sr(i, 1) * 1700 + 100 + Math.sin(f * spd + ph) * 80;
    const y = _sr(i, 2) * 800 + 100 + Math.cos(f * spd * 0.9 + ph) * 50;
    const sz = 40 + _sr(i, 5) * 80;
    const op = (0.03 + _sr(i, 6) * 0.07) * (0.4 + 0.6 * Math.abs(Math.sin(f * 0.012 + ph)));
    return { x, y, sz, op, bl: 20 + sz * 0.3 };
  });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {bokeh.map((b, i) => (
        <div key={i} style={{
          position: 'absolute', left: b.x, top: b.y, width: b.sz, height: b.sz,
          borderRadius: '50%', background: col, opacity: b.op,
          transform: 'translate(-50%,-50%)', filter: `blur(${b.bl}px)`,
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A5. steam_rise — 상승 (파티클 카테고리, scheme prop)
export const SteamRise: React.FC<_SPCS> = ({ f, colors, scheme = 'base' }) => {
  const col = _pcol(colors, scheme);
  const particles = Array.from({ length: 14 }, (_, i) => {
    const cycleLen = 300 + _sr(i, 1) * 200;
    const t = ((f + _sr(i, 2) * cycleLen) % cycleLen) / cycleLen;
    const baseX = (0.1 + _sr(i, 3) * 0.8) * 1920;
    const wobble = Math.sin(f * (0.03 + _sr(i, 4) * 0.02) + _sr(i, 5) * Math.PI * 2) * (15 + _sr(i, 6) * 25);
    const w = 20 + _sr(i, 7) * 40;
    const op = t < 0.1 ? t * 10 * 0.22 : t < 0.5 ? 0.22 : t < 0.8 ? (1 - (t - 0.5) / 0.3) * 0.22 : 0;
    return { x: baseX + wobble, y: 1080 * 0.95 - t * 1200, w, h: 8 + _sr(i, 8) * 16, sc: 0.8 + t * 0.6, op, bl: 12 + w * 0.3 };
  });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: p.x, top: p.y, width: p.w, height: p.h,
          borderRadius: '50%', background: col, opacity: p.op,
          transform: `translate(-50%,-50%) scale(${p.sc})`, filter: `blur(${p.bl}px)`,
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A6. gradient_wave — 흐름 (그라데이션 카테고리 · 씬 톤온톤)
// conic-gradient 는 SVG 네이티브 부재 → 48섹터 × 각도별 알파 보간 근사 (기하 정적 — 모듈 1회 산출,
// 프레임당은 <g rotate> 만 변동). 원본 4-stop 균등: accent1a → transparent → accent10 → transparent.
const _GW_CONIC_R = 1200; // 1920×1080 중심→코너(≈1102) 커버 반경
const _GW_CONIC_SECTORS = Array.from({ length: 48 }, (_, k) => {
  const pt = (a: number) => `${(960 + Math.sin(a) * _GW_CONIC_R).toFixed(1)},${(540 - Math.cos(a) * _GW_CONIC_R).toFixed(1)}`;
  // 섹터 경계는 정확 인접 — 오버랩을 주면 이음새 알파 2배로 스포크 아티팩트 (렌더 스틸 실측)
  const a0 = (k / 48) * Math.PI * 2;
  const a1 = ((k + 1) / 48) * Math.PI * 2;
  const t = (k + 0.5) / 48;
  const alpha = t < 1 / 3 ? (0x1a / 255) * (1 - t * 3) : t < 2 / 3 ? (0x10 / 255) * ((t - 1 / 3) * 3) : (0x10 / 255) * (1 - (t - 2 / 3) * 3);
  return { d: `M960,540 L${pt(a0)} A${_GW_CONIC_R},${_GW_CONIC_R} 0 0 1 ${pt(a1)} Z`, alpha };
});
export const GradientWave: React.FC<_SPC> = ({ f, colors }) => {
  const angle = f * 0.3;
  const x1 = 50 + Math.sin(f * 0.008) * 20, y1 = 50 + Math.cos(f * 0.007) * 20;
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <SvgRadial cx={x1 / 100} cy={y1 / 100} rx={0.80} ry={0.60}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x30 / 255 }, { offset: 0.7, color: colors.accent, opacity: 0 }]} />
      <SvgRadial cx={(100 - x1) / 100} cy={(100 - y1) / 100} rx={0.60} ry={0.50}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x1f / 255 }, { offset: 0.7, color: colors.accent, opacity: 0 }]} />
      <svg width="100%" height="100%" viewBox="0 0 1920 1080" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, opacity: 0.9, pointerEvents: 'none' }}>
        <g transform={`rotate(${angle} 960 540)`}>
          {_GW_CONIC_SECTORS.map((s, i) => (
            <path key={i} d={s.d} fill={colors.accent} fillOpacity={s.alpha} />
          ))}
        </g>
      </svg>
      <SvgRadial cx={0.5} cy={0.5} rx={1} ry={1}
        stops={[{ offset: 0.55, color: '#000000', opacity: 0 }, { offset: 1, color: '#000000', opacity: 0.12 }]} />
    </AbsoluteFill>
  );
};

// A7. light_shift — 흐름 경량 (그라데이션 카테고리 · 씬 톤온톤)
export const LightShift: React.FC<_SPC> = ({ f, colors }) => {
  const x1 = 30 + Math.sin(f * 0.006) * 20, y1 = 30 + Math.cos(f * 0.008) * 20;
  const x2 = 70 + Math.cos(f * 0.007) * 15, y2 = 70 + Math.sin(f * 0.006) * 15;
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <SvgRadial cx={x1 / 100} cy={y1 / 100} rx={0.60} ry={0.55}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x33 / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]} />
      <SvgRadial cx={x2 / 100} cy={y2 / 100} rx={0.50} ry={0.48}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x22 / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]} />
      <SvgRadial cx={0.5} cy={0.5} rx={0.35} ry={0.30}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x14 / 255 }, { offset: 1, color: colors.accent, opacity: 0 }]} />
    </AbsoluteFill>
  );
};

// A8. soft_light_rays — 빛줄기 (파티클 카테고리, scheme prop)
export const SoftLightRays: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const col = _pcol(colors, scheme);
  const rays = Array.from({ length: 4 }, (_, i) => ({
    x: _sr(i, 1) * 1600 + 160 + Math.sin(f * (0.006 + _sr(i, 2) * 0.003) + _sr(i, 3) * Math.PI * 2) * 60,
    op: (0.10 + _sr(i, 4) * 0.18) * (0.5 + 0.5 * Math.abs(Math.sin(f * 0.01 + _sr(i, 5) * Math.PI * 2))),
    angle: 30 + _sr(i, 6) * 15,
    w: 160 + _sr(i, 7) * 80,
  }));
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {rays.map((r, i) => (
        <div key={i} style={{
          position: 'absolute', left: r.x, top: -200, bottom: -200, width: r.w,
          background: `linear-gradient(to bottom, transparent, ${col}55, transparent)`,
          opacity: r.op, transform: `rotate(${r.angle}deg)`, filter: 'blur(50px)',
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A9. light_leaks — A★ 전용 임팩트 (그라데이션 카테고리 · 씬 톤온톤 + 화이트 코어)
export const LightLeaks: React.FC<_SPC> = ({ f, colors }) => {
  const op1 = interpolate(f, [_SD * 0.15, _SD * 0.25, _SD * 0.5, _SD * 0.85, _SD], [0, 0.8, 1, 0.5, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const op2 = interpolate(f, [_SD * 0.2, _SD * 0.35, _SD * 0.6, _SD * 0.9, _SD], [0, 0.7, 0.8, 0.6, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const lx1 = 50 + Math.sin(f * 0.01) * 10, ly1 = 30 + Math.cos(f * 0.008) * 10;
  const lx2 = 40 + Math.cos(f * 0.009) * 12, ly2 = 50 + Math.sin(f * 0.011) * 12;
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <SvgRadial cx={lx1 / 100} cy={ly1 / 100} rx={0.70} ry={0.60}
        stops={[
          { offset: 0, color: colors.accent, opacity: 0xcc / 255 },
          { offset: 0.4, color: colors.accent, opacity: 0x66 / 255 },
          { offset: 0.75, color: colors.accent, opacity: 0 },
        ]}
        style={{ opacity: op1, mixBlendMode: 'screen' }} />
      <SvgRadial cx={lx2 / 100} cy={ly2 / 100} rx={0.60} ry={0.55}
        stops={[
          { offset: 0, color: '#FFFFFF', opacity: 0.6 },
          { offset: 0.35, color: colors.accent, opacity: 0x55 / 255 },
          { offset: 0.7, color: colors.accent, opacity: 0 },
        ]}
        style={{ opacity: op2, mixBlendMode: 'screen' }} />
    </AbsoluteFill>
  );
};

// A10. particle_rise — 상승 (파티클 카테고리, scheme prop)
export const ParticleRise: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const col = _pcol(colors, scheme);
  const pts = Array.from({ length: 28 }, (_, i) => {
    const spd = (0.3 + _sr(i, 1) * 0.8) * 1.5;
    const loopRange = 1400;
    const rawY = 1100 - ((f * spd + _sr(i, 2) * loopRange) % loopRange);
    const prog = 1 - rawY / 1100;
    const szBase = 2 + _sr(i, 4) * 18;
    const szMult = prog < 0.3 ? 0.4 + (prog / 0.3) * 0.9 : prog < 0.6 ? 1.3 - ((prog - 0.3) / 0.3) * 0.2 : 1.1 - ((prog - 0.6) / 0.4) * 0.3;
    const wobble = Math.sin(f * (0.02 + _sr(i, 5) * 0.01) + _sr(i, 6) * Math.PI * 2) * 20;
    const op = interpolate(prog, [0, 0.05, 0.85, 1], [0, 0.4 + _sr(i, 7) * 0.3, 0.4 + _sr(i, 7) * 0.3, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const sz = szBase * szMult;
    return { x: (0.05 + _sr(i, 3) * 0.9) * 1920 + wobble, y: rawY, sz, op, bl: sz > 14 ? 1 : 0 };
  });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {pts.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: p.x, top: p.y, width: p.sz, height: p.sz,
          borderRadius: '50%', background: col, opacity: p.op,
          transform: 'translate(-50%,-50%)', filter: p.bl ? `blur(${p.bl}px)` : 'none',
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A10-b. confetti — 회전하는 직사각형 낙하 (파티클 카테고리, scheme prop)
//   ★색종이 결은 «직사각형 + 회전»이다★ — 원형 입자(ParticleRise·FloatingOrbs)로는 안 난다.
//   숫자(개수·크기·속도·회전)는 ★PATTERN.confetti 가 정본★ — 여기서 다시 쓰지 않는다.
//   색은 톤온톤 규약을 지킨다(§파티클 카테고리 = scheme 으로 base/accent) — 여러 색을 섞지 않고
//   ★단색★ 으로 두고, 결은 회전·크기·속도 변주로만 낸다.
//   ★accentDim 교차를 걷어냈다(2026-09-09 회장님 확정)★ — 처음엔 «톤온톤 안의 변주» 로 조각의 1/3 을
//   accentDim 으로 뒀는데, 대비를 재보니 배경 대비 **1.17~1.35 : 1** 로 사실상 보이지 않았다
//   (accent 는 16:9 9.48:1 · 9:16 2.31:1). 변주 효과는 0 이면서 보이는 조각만 2/3 로 줄이던 코드다.
export const Confetti: React.FC<_SPCS> = ({ f, colors, scheme = 'accent' }) => {
  const S = PATTERN.confetti;
  const col = _pcol(colors, scheme);
  const pcs = Array.from({ length: S.count }, (_, i) => {
    const spd = S.speedMin + _sr(i, 1) * (S.speedMax - S.speedMin);
    // 낙하 루프 — 화면(1080) 위아래로 여유를 둬 등장·퇴장이 잘리지 않는다.
    const loopRange = 1400;
    const y = ((f * spd * 6 + _sr(i, 2) * loopRange) % loopRange) - 200;
    const w = S.sizeWMin + _sr(i, 3) * (S.sizeWMax - S.sizeWMin);
    const h = S.sizeHMin + _sr(i, 4) * (S.sizeHMax - S.sizeHMin);
    const rot = f * (S.rotSpeedMin + _sr(i, 5) * (S.rotSpeedMax - S.rotSpeedMin)) + _sr(i, 6) * 360;
    const sway = Math.sin(f * 0.02 + _sr(i, 7) * Math.PI * 2) * 26; // 낙하 중 좌우 흔들림
    return {
      x: (0.03 + _sr(i, 8) * 0.94) * 1920 + sway,
      y, w, h, rot,
      op: 0.45 + _sr(i, 9) * 0.4,
    };
  });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }} data-confetti="">
      {pcs.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: p.x, top: p.y, width: p.w, height: p.h,
          background: col, opacity: p.op,
          transform: `translate(-50%,-50%) rotate(${p.rot}deg)`,
        }} />
      ))}
    </AbsoluteFill>
  );
};

// A11. vignette_pulse — 맥동 (그라데이션 카테고리 · 비네트 + 씬 톤온톤 글로우)
// BPM 가속: 60 → 100 bpm over _SD frames
export const VignettePulse: React.FC<_SPC> = ({ f, colors }) => {
  const sec = f / 30;
  const bpm = 60 + sec * (40 / (_SD / 30));
  const beat = Math.pow(Math.abs(Math.sin(sec * (bpm / 60) * Math.PI)), 0.5);
  const vSz = 35 + beat * 15;
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <SvgRadial cx={0.5} cy={0.5} rx={vSz / 100} ry={(vSz * 0.75) / 100}
        stops={[
          { offset: 0, color: '#000000', opacity: 0 },
          { offset: 0.45, color: '#000000', opacity: 0.65 },
          { offset: 1, color: '#000000', opacity: 0.96 },
        ]}
        style={{ opacity: 0.6 + beat * 0.4 }} />
      <SvgRadial cx={0.5} cy={0.5} rx={(vSz * 1.5) / 100} ry={(vSz * 1.2) / 100}
        stops={[{ offset: 0, color: colors.accent, opacity: 0x73 / 255 }, { offset: 0.7, color: colors.accent, opacity: 0 }]}
        style={{ opacity: beat * 0.6, mixBlendMode: 'screen' }} />
    </AbsoluteFill>
  );
};

// ─── ATMOSPHERE B (등장 악센트 · Wrapper 패턴 · 씬당 최대 1종) ────────────────
// V2.22 변경: AbsoluteFill 중앙 정렬 → element wrapper로 전환
// 콘텐츠 등장 타이밍·위치에 자동 정렬 (떡하니 중앙 노출 X)
// 사용: <GlowPulse f={f - revealAt} colors={C}>{ ...infographic element ... }</GlowPulse>
// 또는 dispatch: <RevealAccent id="glow_pulse" f={f - revealAt} colors={C}>...</RevealAccent>

// B1. glow_pulse — 원형 글로우 번쩍→소멸 (Pop·Spring·Squeeze 모션용)
// 콘텐츠 뒤에 깔리는 후광. 4-stop radial gradient · 씬 accent 색상 · element 위치에 자동 정렬
export const GlowPulse: React.FC<_SPC & { children: React.ReactNode }> = ({ f, colors, children }) => {
  const glowOp = interpolate(f, [0, 5, 30, 60], [0, 0.85, 0.5, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const glowSc = interpolate(f, [0, 60], [1.3, 1.75], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: `translate(-50%, -50%) scale(${glowSc})`,
        width: 600, height: 450,
        opacity: glowOp, pointerEvents: 'none',
      }}>
        {/* ellipse at center 무사이즈=farthest-corner ≈ 반경 0.71 근사 */}
        <SvgRadial cx={0.5} cy={0.5} rx={0.71} ry={0.71}
          stops={[
            { offset: 0, color: colors.accent, opacity: 0x80 / 255 },
            { offset: 0.15, color: colors.accent, opacity: 0x50 / 255 },
            { offset: 0.35, color: colors.accent, opacity: 0x30 / 255 },
            { offset: 0.55, color: colors.accent, opacity: 0x08 / 255 },
            { offset: 1, color: colors.accent, opacity: 0 },
          ]} />
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
};

// B2. particle_burst — 방사형 입자 폭발 (Slam·Drop 모션용)
// 콘텐츠 중심에서 외곽으로 폭발 · 착지 완료 시점에 트리거 · 씬 accent
export const ParticleBurst: React.FC<_SPC & { children: React.ReactNode }> = ({ f, colors, children }) => {
  const distMult = interpolate(f, [0, 5, 35], [0, 1, 2.5], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const burst = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2 + _sr(i, 1) * 0.5;
    const dist = (80 + _sr(i, 2) * 120) * distMult;
    const op = interpolate(f, [0, 3, 18, 40], [0, 0.9, 0.5, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return { bx: Math.cos(angle) * dist, by: Math.sin(angle) * dist, op, sz: 4 + _sr(i, 3) * 8, rot: f * 6, shape: _sr(i, 5) < 0.6 ? '50%' : '2px' };
  });
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
      <div style={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0, pointerEvents: 'none' }}>
        {burst.map((p, i) => (
          <div key={i} style={{
            position: 'absolute', width: p.sz, height: p.sz,
            borderRadius: p.shape, background: colors.accent, opacity: p.op,
            transform: `translate(${p.bx - p.sz / 2}px, ${p.by - p.sz / 2}px) rotate(${p.rot}deg)`,
            boxShadow: `0 0 ${p.sz}px ${colors.accent}80`,
          }} />
        ))}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
};

// B3. ring_ripple — 동심원 2-3개 확장 소멸 (Pin Drop·Pop 모션용)
// 콘텐츠 중심에서 확장 ripple. 아이콘·포인트 강조 · 씬 accent
export const RingRipple: React.FC<_SPC & { children: React.ReactNode }> = ({ f, colors, children }) => {
  const rings = [0, 0.15 * 30, 0.30 * 30].map((delay, i) => {
    const prog = Math.min(1, Math.max(0, f - delay) / (1.2 * 30));
    const sz = prog * [200, 320, 440][i];
    const op = prog < 0.1 ? prog * 7 : interpolate(prog, [0.1, 1], [0.7, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return { sz, op };
  });
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
      <div style={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0, pointerEvents: 'none' }}>
        {rings.map((r, i) => (
          <div key={i} style={{
            position: 'absolute', width: r.sz, height: r.sz, borderRadius: '50%',
            border: `2px solid ${colors.accent}`, opacity: r.op,
            boxShadow: `0 0 ${12 + i * 6}px ${colors.accentGlow}`,
            transform: `translate(-50%, -50%)`,
          }} />
        ))}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
};

// ─── 텍스트 종속 디코 (Wrapper 패턴 · 자동 너비 매칭) ──────────
// 절대 단독 사용 금지 — 반드시 텍스트(또는 텍스트 element)를 children으로 감쌈
// 부동 라인·박스 금지 룰: §8-0 하드룰 참조

// UnderlineSweep — 텍스트 아래 언더라인 좌→우 scaleX 리빌
// accent_line_sweep 후속. 텍스트 너비 자동 매칭 (display: inline-block)
// 사용: <UnderlineSweep f={f - revealAt} colors={C}>핵심 텍스트</UnderlineSweep>
export const UnderlineSweep: React.FC<_SPC & { children: React.ReactNode }> = ({ f, colors, children }) => {
  const { durationFrames, heightPx, gapPx } = PATTERN.underlineSweep;
  const sc = interpolate(f, [0, durationFrames], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      <span style={{
        position: 'absolute', left: 0, right: 0, bottom: -gapPx,
        height: heightPx,
        background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`,
        transformOrigin: 'left center', transform: `scaleX(${sc})`,
      }} />
    </span>
  );
};

// SwipeHighlight — 텍스트 배경에 컬러 fill 좌→우 clipPath 리빌
// 텍스트 너비 자동 매칭. children은 텍스트(또는 텍스트 element) 필수
// 사용: <SwipeHighlight f={f - revealAt} colors={C}>강조 구간</SwipeHighlight>
export const SwipeHighlight: React.FC<_SPC & { children: React.ReactNode }> = ({ f, colors, children }) => {
  const { durationFrames } = PATTERN.swipeHighlight;
  const prog = interpolate(f, [0, durationFrames], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{
        position: 'absolute', inset: 0,
        background: colors.accentDim,
        clipPath: `inset(0 ${(1 - prog) * 100}% 0 0)`,
      }} />
      <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
    </span>
  );
};

// BoxSliderHighlight — 텍스트 배경에 박스 슬라이드 + 텍스트 컬러 플립
// 텍스트 너비 자동 매칭. flipColor는 박스가 50% 이상 덮였을 때 텍스트가 바뀔 색
// 사용: <BoxSliderHighlight f={f - revealAt} colors={C} flipColor="#FFFFFF">강조</BoxSliderHighlight>
export const BoxSliderHighlight: React.FC<_SPC & { children: React.ReactNode; flipColor?: string }> = ({ f, colors, children, flipColor }) => {
  const { durationFrames, boxOpacity, textColorThreshold, borderRadius } = PATTERN.boxSliderHighlight;
  const prog = interpolate(f, [0, durationFrames], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <span style={{ position: 'relative', display: 'inline-block', borderRadius }}>
      <span style={{
        position: 'absolute', inset: 0,
        background: colors.accent, opacity: boxOpacity, borderRadius,
        clipPath: `inset(0 ${(1 - prog) * 100}% 0 0)`,
      }} />
      <span style={{ position: 'relative', zIndex: 1, color: prog > textColorThreshold && flipColor ? flipColor : undefined }}>{children}</span>
    </span>
  );
};

// ─── VFX (후처리 · 씬당 최대 3종, V/VM 전용 · M 씬 금지) ───────
// z-order: video → VFX → scrim → infographic (VFX는 영상 효과 영역까지만)

// VFX 1. film_grain — 필름 질감 노이즈 (overlay blendMode · 컬러 무관)
// ★노이즈는 svg 내부 rect 에 직접 적용 (렌더-세이프): 구 구현(빈 div + CSS filter:url(#svg필터))은
//   web-renderer 렌더 캡처에서 안 칠해진다 (2026-07-16 스틸 실측 0.00% — SVG 내부 필터는 페인트됨).
export const FilmGrain: React.FC<_SP> = ({ f }) => (
  <AbsoluteFill style={{ pointerEvents: 'none' }}>
    <svg width="100%" height="100%" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, opacity: 0.62, mixBlendMode: 'overlay' }}>
      <filter id={`grain-${f}`} x="0%" y="0%" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency={0.65} numOctaves={3} seed={f * 3 + 7} stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect x="0" y="0" width="100%" height="100%" filter={`url(#grain-${f})`} />
    </svg>
  </AbsoluteFill>
);

// VFX 2. chromatic_aberration — RGB 채널 분리 (충격 표현 · 표준 RGB shift, 컬러 무관)
// IF=45f를 임팩트 anchor로 사용. 다른 시점이면 f 전달 시 offset 조정
export const ChromaticAberration: React.FC<_SP> = ({ f }) => {
  const IF = 45;
  const shift = interpolate(f, [IF - 15, IF, IF + 3, IF + 24], [0, 0, 8, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const vfxOp = interpolate(f, [IF - 15, IF, _SD], [0, 0.8, 0.5], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <AbsoluteFill style={{ backgroundColor: `rgba(255,0,0,${0.3 * vfxOp})`, transform: `translateX(${-shift}px)`, mixBlendMode: 'screen' }} />
      <AbsoluteFill style={{ backgroundColor: `rgba(0,255,255,${0.3 * vfxOp})`, transform: `translateX(${shift}px)`, mixBlendMode: 'screen' }} />
    </AbsoluteFill>
  );
};

// VFX 3. horizontal_glitch — 수평 디지털 오류 (디지털 글리치 표준 색, 컬러 무관)
export const HorizontalGlitch: React.FC<_SP> = ({ f }) => {
  const glitchSeed = Math.sin(f * 0.23) * Math.cos(f * 0.41);
  const layers = [
    { thresh: 0.5, rangeX: 20, yFrom: 10, yTo: 60, hFrom: 1, hTo: 4, col: 'rgba(255,255,255,1)', blend: 'screen'  as const, op: 0.12 },
    { thresh: 0.6, rangeX: 15, yFrom: 30, yTo: 80, hFrom: 0.5, hTo: 2.5, col: 'rgba(200,30,30,1)', blend: 'screen'  as const, op: 0.10 },
    { thresh: 0.3, rangeX: 30, yFrom: 5,  yTo: 95, hFrom: 2, hTo: 6, col: 'rgba(255,255,255,1)', blend: 'overlay' as const, op: 0.15 },
  ];
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      {f > 20 && layers.map((l, i) => {
        if (glitchSeed + i * 0.3 <= l.thresh) return null;
        const gy = (Math.sin(f * (0.15 + i * 0.07)) * 0.5 + 0.5) * (l.yTo - l.yFrom) + l.yFrom;
        const gh = l.hFrom + Math.abs(Math.sin(f * (0.1 + i * 0.05))) * (l.hTo - l.hFrom);
        const gx = Math.sin(f * (0.3 + i * 0.1)) * l.rangeX;
        return <div key={i} style={{ position: 'absolute', top: `${gy}%`, left: 0, right: 0, height: `${gh}%`, backgroundColor: l.col, opacity: l.op, transform: `translateX(${gx}px)`, mixBlendMode: l.blend }} />;
      })}
    </AbsoluteFill>
  );
};

// VFX 4. strobe_flash — 고주파 밝기 펄스 (경고등·비상 · 화이트 표준, 컬러 무관)
// IMPACT=45f anchor (1.5s @ 30fps)
export const StrobeFlash: React.FC<_SP> = ({ f }) => {
  const sec = f / 30;
  const IMPACT = 45 / 30;
  const inWindow = sec >= IMPACT - 0.3 && sec <= IMPACT + 0.5;
  const raw = Math.sin(f * 2.5) > 0.6 ? 1 : 0;
  const env = interpolate(sec, [IMPACT - 0.3, IMPACT, IMPACT + 0.1, IMPACT + 0.5], [0.05, 0.2, 0.15, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <AbsoluteFill style={{ backgroundColor: 'white', opacity: inWindow ? raw * env : 0, mixBlendMode: 'screen' }} />
    </AbsoluteFill>
  );
};

// VFX 5. radial_zoom_blur — 방사형 충격파 (폭발·충돌 1회성 · 화이트 표준, 컬러 무관)
// IMPACT=40f anchor
export const RadialZoomBlur: React.FC<_SP> = ({ f }) => {
  const sec = f / 30;
  const IMPACT = 40 / 30;
  const rOp = interpolate(sec, [IMPACT, IMPACT + 0.03, IMPACT + 0.15, IMPACT + 0.8], [0, 0.6, 0.4, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const rSc = interpolate(sec, [IMPACT, IMPACT + 0.5], [0.8, 2.0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  // ★CSS radial → SvgRadial (렌더-세이프 — SvgRadial 주석 참조). impact 페르소나 autoVfx 로
  //   M 씬에서도 발동되는 경로라 ambient 와 같은 계열 수리. transform/opacity 는 div 래퍼 유지
  //   (glow_pulse 와 동일 구조 — 렌더 스틸 실측 PASS 패턴).
  return (
    <AbsoluteFill style={{ overflow: 'hidden', pointerEvents: 'none' }}>
      <AbsoluteFill style={{ opacity: rOp, transform: `scale(${rSc})` }}>
        {/* ellipse at center 무사이즈=farthest-corner ≈ 반경 0.71 근사 */}
        <SvgRadial cx={0.5} cy={0.5} rx={0.71} ry={0.71}
          stops={[
            { offset: 0.1, color: '#FFFFFF', opacity: 0 },
            { offset: 0.4, color: '#FFFFFF', opacity: 0.8 },
            { offset: 0.6, color: '#C8C8C8', opacity: 0.4 },
            { offset: 0.8, color: '#C8C8C8', opacity: 0 },
          ]} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// VFX 6. video_scale_wiggle — 핸드헬드 카메라 떨림 (V/VM 전용, wrapper 패턴)
// 다른 VFX와 달리 컨텐츠를 감싸는 wrapper로 사용. VfxLayer dispatch에 포함되지 않음
//   <VideoScaleWiggle f={f}><VideoCard ... /></VideoScaleWiggle>
export const VideoScaleWiggle: React.FC<_SP & { children?: React.ReactNode; totalFrames?: number }> = ({ f, children, totalFrames = _SD }) => {
  const sec = f / 30, totalSec = totalFrames / 30;
  const IMPACT = 45 / 30;
  const sc = interpolate(sec, [0, IMPACT - 0.5, IMPACT, totalSec - 1, totalSec], [1, 1.08, 1.15, 1.05, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const wi = interpolate(sec, [0, IMPACT - 0.5, IMPACT, IMPACT + 0.3, totalSec - 1, totalSec], [0, 3, 12, 8, 2, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const wx = (Math.sin(f * 0.7) * 0.5 + Math.sin(f * 1.3) * 0.3 + Math.cos(f * 2.1) * 0.2) * wi;
  const wy = (Math.cos(f * 0.9) * 0.4 + Math.sin(f * 1.7) * 0.35 + Math.cos(f * 2.7) * 0.25) * wi;
  const wr = Math.sin(f * 0.5) * 0.15 * (wi / 12);
  return (
    <AbsoluteFill style={{ transform: `scale(${sc}) translate(${wx}px, ${wy}px) rotate(${wr}deg)`, transformOrigin: 'center center' }}>
      {children}
    </AbsoluteFill>
  );
};

// V2.23 추가 VFX — 모두 transparent overlay (배경은 PersonaLayer가 깔아둠)

// VFX 7. white_flash — 진입 시 백색 플래시 (entry timing)
export const WhiteFlash: React.FC<_SP> = ({ f }) => {
  const flashOp = interpolate(f, [0, 4, 8, 22], [1, 1, 0.6, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, background: '#ffffff', opacity: flashOp, mixBlendMode: 'screen' }} />
    </AbsoluteFill>
  );
};

// VFX 8. vhs_distort — VHS 색번짐 + 노이즈 + 트래킹 (persistent)
export const VhsDistort: React.FC<_SP> = ({ f }) => {
  const tracking = ((f * 6) % 240) - 120; // 위아래 사이클
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,0,80,0.14)', transform: 'translateX(-3px)', mixBlendMode: 'screen' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,180,255,0.14)', transform: 'translateX(3px)',  mixBlendMode: 'screen' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.14) 0px, rgba(0,0,0,0.14) 2px, transparent 2px, transparent 4px)', mixBlendMode: 'overlay' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${tracking}%`, height: '8%', background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.26), transparent)', mixBlendMode: 'screen', filter: 'blur(2px)' }} />
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <filter id="vfx-vhs-noise-token">
          <feTurbulence type="fractalNoise" baseFrequency={0.9} numOctaves={2} seed={f * 7 + 13} stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </svg>
      <div style={{ position: 'absolute', inset: 0, filter: 'url(#vfx-vhs-noise-token)', opacity: 0.16, mixBlendMode: 'overlay' }} />
    </AbsoluteFill>
  );
};

// VFX 9. scan_distort — 가로 스캔라인 이동 (persistent · 데이터 무드)
export const ScanDistort: React.FC<_SP> = ({ f }) => {
  const scanProgress = ((f * 2) % 240) / 240;
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 3px)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${scanProgress * 100}%`, height: 80, background: `linear-gradient(180deg, transparent, ${ACCENT.sky.onDark.accent}55 50%, transparent)`, filter: 'blur(4px)', mixBlendMode: 'screen' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${scanProgress * 100}%`, height: 200, background: 'repeating-linear-gradient(0deg, rgba(135, 218, 255, 0.10) 0px, rgba(135, 218, 255, 0.10) 1px, transparent 1px, transparent 4px)', transform: 'translateY(-100px)' }} />
    </AbsoluteFill>
  );
};

// ─── Dispatch — MSB bgComponents/revealAccent/vfxLayers 키 → 컴포넌트 ─

type AtmosphereAKey      = keyof typeof ATMOSPHERE_A;
export type RevealAccentKey = keyof typeof ATMOSPHERE_B;  // glow_pulse | particle_burst | ring_ripple
export type AtmosphereKey   = AtmosphereAKey;            // bg ambient만 (ATMOSPHERE_B는 wrapper로 분리)
type VfxKey                = keyof typeof VFX_CATALOG;

// 카테고리 분류: 그라데이션(_SPC) vs 파티클(_SPCS · scheme 받음)
const _PARTICLE_KEYS: ReadonlySet<AtmosphereKey> = new Set([
  'css_particle_glow', 'floating_orbs', 'warm_bokeh', 'steam_rise', 'soft_light_rays', 'particle_rise',
]);

const ATMOSPHERE_GRADIENT: Partial<Record<AtmosphereKey, React.FC<_SPC>>> = {
  warm_glow:      WarmGlow,
  gradient_wave:  GradientWave,
  light_shift:    LightShift,
  light_leaks:    LightLeaks,
  vignette_pulse: VignettePulse,
};

const ATMOSPHERE_PARTICLE: Partial<Record<AtmosphereKey, React.FC<_SPCS>>> = {
  css_particle_glow: CssParticleGlow,
  floating_orbs:     FloatingOrbs,
  warm_bokeh:        WarmBokeh,
  steam_rise:        SteamRise,
  soft_light_rays:   SoftLightRays,
  particle_rise:     ParticleRise,
};

// MSB atmosphere.bgComponents 디스패치 (M 씬 배경 ambient 레이어 · V/VM 금지)
// colors: 씬 C 객체 그대로 전달 (bg·panel·accent·accentDim·accentGlow 필요)
// scheme: 파티클 카테고리에만 적용 ('base' = 톤온톤 / 'accent' = 씬 accent)
// 사용: {atmosphere.bgComponents?.map(id => <AtmosphereLayer key={id} id={id} f={f} colors={C} scheme={atmosphere.colorScheme} />)}
export const AtmosphereLayer: React.FC<{
  id: AtmosphereKey;
  f: number;
  colors: AtmosphereColors;
  scheme?: 'base' | 'accent';
}> = ({ id, f, colors, scheme }) => {
  if (_PARTICLE_KEYS.has(id)) {
    const Comp = ATMOSPHERE_PARTICLE[id];
    return Comp ? <Comp f={f} colors={colors} scheme={scheme} /> : null;
  }
  const Comp = ATMOSPHERE_GRADIENT[id];
  return Comp ? <Comp f={f} colors={colors} /> : null;
};

// MSB infographic.elements[].revealAccent 디스패치 (element wrapper · element 위치·타이밍 자동 정렬)
// 사용: <RevealAccent id={el.revealAccent} f={f - revealAt} colors={C}>{element 콘텐츠}</RevealAccent>
// 패턴 없는 element는 그냥 children 그대로 렌더
export const RevealAccent: React.FC<{
  id: RevealAccentKey | null | undefined;
  f: number;
  colors: AtmosphereColors;
  children: React.ReactNode;
}> = ({ id, f, colors, children }) => {
  if (id === 'glow_pulse')     return <GlowPulse     f={f} colors={colors}>{children}</GlowPulse>;
  if (id === 'particle_burst') return <ParticleBurst f={f} colors={colors}>{children}</ParticleBurst>;
  if (id === 'ring_ripple')    return <RingRipple    f={f} colors={colors}>{children}</RingRipple>;
  return <>{children}</>;
};

// video_scale_wiggle은 wrapper 패턴이라 dispatch에서 제외
// 모든 VFX는 표준 컬러(white/red/cyan/glitch RGB)를 사용 — 씬 컬러 props 받지 않음
const VFX_MAP: Partial<Record<VfxKey, React.FC<_SP>>> = {
  film_grain:           FilmGrain,
  chromatic_aberration: ChromaticAberration,
  horizontal_glitch:    HorizontalGlitch,
  strobe_flash:         StrobeFlash,
  radial_zoom_blur:     RadialZoomBlur,
  white_flash:          WhiteFlash,
  vhs_distort:          VhsDistort,
  scan_distort:         ScanDistort,
};

// MSB vfxLayers.videoOnly 디스패치 (V/VM 전용 · M 씬에서 호출 금지)
// z-order: 영상 위, scrim/infographic 아래 (영상 효과 영역까지만)
// 사용: {vfxLayers?.videoOnly?.filter(id => id !== 'video_scale_wiggle').map(id => <VfxLayer key={id} id={id} f={f} />)}
// 단, video_scale_wiggle은 VideoScaleWiggle을 wrapper로 직접 사용
export const VfxLayer: React.FC<{ id: VfxKey; f: number }> = ({ id, f }) => {
  const Comp = VFX_MAP[id];
  return Comp ? <Comp f={f} /> : null;
};

// ──────────────────────────────────────────────────────────────────
// ─── PERSONA CATALOG (V2.23) ──────────────────────────────────────
// ──────────────────────────────────────────────────────────────────
// 페르소나 = 분류 애매한 atomic 토큰(ambient/pattern/vfx/emphasis)을
// 무드 단위로 묶은 묶음. MSB가 씬별로 persona 선언 → PersonaLayer가 자동 합성.
// MSB context(mood/sceneType/contentTone) → fits 매칭으로 호환 페르소나만 노출.

export type PersonaId =
  | 'opening' | 'focusing' | 'analytical' | 'compliance' | 'tension'
  | 'critical' | 'impact' | 'resolution' | 'reflection' | 'celebration';

export type ColorTone       = 'warm' | 'cool' | 'neutral';
export type ColorBrightness = 'high' | 'mid-high' | 'mid' | 'low';
export type ColorSaturation = 'high' | 'mid' | 'low';
export type AccentEnum      = 'brand' | 'data' | 'caution' | 'alert' | 'success' | null;
export type SceneBase       = 'light' | 'dark';
export type PatternId       = 'dotGrid' | 'lineGrid' | 'hexGrid' | 'diagonalLines' | 'circuit_lines' | 'topo_lines';
export type Intensity       = 'sm' | 'md' | 'lg';

// VFX 확장 — 기존 VFX_CATALOG + 신규 3종 (white_flash, vhs_distort, scan_distort)
export type VfxIdExt =
  | 'film_grain' | 'chromatic_aberration' | 'horizontal_glitch'
  | 'strobe_flash' | 'radial_zoom_blur' | 'video_scale_wiggle'
  | 'white_flash' | 'vhs_distort' | 'scan_distort';

// Emphasis 통합 — 요소(element) + 키워드(keyword)
export type EmphasisId =
  | 'glow_pulse' | 'ring_ripple' | 'particle_burst' | 'floating_motes' | 'light_streak'
  | 'accent_line_sweep' | 'scale_stamp' | 'confetti'
  | 'swipe_highlight' | 'box_slider_highlight' | 'underline_sweep' | 'scanlines_keyword';

export type PersonaSpec = {
  color: {
    tone: ColorTone;
    brightness: ColorBrightness;
    saturation: ColorSaturation;
    accent: AccentEnum;
  };
  base: SceneBase;
  ambient: AtmosphereKey;
  pattern: { type: PatternId; intensity: Intensity } | null;
  vfx: VfxIdExt[];              // 추천 vocabulary — 씬 코드가 모먼트별 트리거
  emphasis: EmphasisId[];        // 추천 vocabulary — MSB keyword/element 강조 시 선택
  fits: {
    mood: string[];
    sceneType: ('M' | 'VM' | 'V')[];
    contentTone: string[];
  };
};

// accent enum → ACCENT 토큰 매핑
// brand 기본 = ACCENT.blue (현대건설 톤). 브랜드 토큰 분리 시 이 매핑만 갈아끼우면 됨.
type AccentSurface = { accent: string; accentGlow: string; accentDim: string };
type AccentPair   = { onDark: AccentSurface; onLight: AccentSurface };

export const ACCENT_MAP: Record<NonNullable<AccentEnum>, AccentPair> = {
  brand:   ACCENT.blue,
  data:    ACCENT.cyan,
  caution: ACCENT.amber,
  alert:   ACCENT.red,
  success: ACCENT.green,
} as const;

export const PERSONA_CATALOG: Record<PersonaId, PersonaSpec> = {
  opening: {
    color:    { tone: 'neutral', brightness: 'mid', saturation: 'low', accent: 'brand' },
    base:     'light',
    ambient:  'gradient_wave',
    pattern:  { type: 'dotGrid', intensity: 'sm' },
    vfx:      ['white_flash'],
    emphasis: ['light_streak', 'underline_sweep'],
    fits:     { mood: ['opening', 'intro', 'title'], sceneType: ['M', 'VM', 'V'], contentTone: ['neutral'] },
  },
  focusing: {
    color:    { tone: 'cool', brightness: 'mid', saturation: 'low', accent: 'brand' },
    base:     'dark',
    ambient:  'floating_orbs',
    pattern:  { type: 'dotGrid', intensity: 'sm' },
    vfx:      [],
    emphasis: ['underline_sweep', 'glow_pulse'],
    fits:     { mood: ['info', 'instruction', 'learning', 'explain'], sceneType: ['M', 'VM'], contentTone: ['neutral'] },
  },
  analytical: {
    color:    { tone: 'cool', brightness: 'mid-high', saturation: 'mid', accent: 'data' },
    base:     'dark',
    ambient:  'css_particle_glow',
    pattern:  { type: 'lineGrid', intensity: 'md' },
    vfx:      ['scan_distort'],
    emphasis: ['box_slider_highlight', 'ring_ripple', 'scanlines_keyword'],
    fits:     { mood: ['data', 'analysis', 'statistic', 'comparison'], sceneType: ['M', 'VM'], contentTone: ['neutral', 'positive'] },
  },
  compliance: {
    color:    { tone: 'neutral', brightness: 'mid', saturation: 'low', accent: 'brand' },
    base:     'dark',
    ambient:  'css_particle_glow',
    pattern:  { type: 'hexGrid', intensity: 'sm' },
    vfx:      [],
    emphasis: ['box_slider_highlight', 'glow_pulse'],
    fits:     { mood: ['compliance', 'certification', 'formal', 'official'], sceneType: ['M', 'VM'], contentTone: ['neutral', 'positive'] },
  },
  tension: {
    color:    { tone: 'warm', brightness: 'low', saturation: 'mid', accent: 'caution' },
    base:     'dark',
    ambient:  'vignette_pulse',
    pattern:  null,
    vfx:      ['chromatic_aberration'],
    emphasis: ['underline_sweep'],
    fits:     { mood: ['tension', 'foreshadow', 'risk', 'buildup'], sceneType: ['M', 'VM', 'V'], contentTone: ['negative'] },
  },
  critical: {
    color:    { tone: 'warm', brightness: 'low', saturation: 'high', accent: 'alert' },
    base:     'dark',
    ambient:  'warm_glow',
    pattern:  null,
    vfx:      ['horizontal_glitch', 'video_scale_wiggle'],
    emphasis: ['box_slider_highlight', 'ring_ripple'],
    fits:     { mood: ['warning', 'incident', 'danger', 'alert'], sceneType: ['M', 'VM', 'V'], contentTone: ['urgent', 'negative'] },
  },
  impact: {
    color:    { tone: 'neutral', brightness: 'high', saturation: 'high', accent: 'alert' },
    base:     'dark',
    ambient:  'vignette_pulse',
    pattern:  null,
    vfx:      ['white_flash', 'strobe_flash', 'radial_zoom_blur'],
    emphasis: ['particle_burst'],
    fits:     { mood: ['impact', 'accident', 'climax', 'shock'], sceneType: ['M', 'VM', 'V'], contentTone: ['urgent', 'shock'] },
  },
  resolution: {
    color:    { tone: 'cool', brightness: 'mid-high', saturation: 'mid', accent: 'success' },
    base:     'light',
    ambient:  'light_leaks',
    pattern:  { type: 'topo_lines', intensity: 'sm' },
    vfx:      ['white_flash'],
    emphasis: ['swipe_highlight', 'light_streak'],
    fits:     { mood: ['solution', 'resolution', 'positive', 'transition'], sceneType: ['M', 'VM'], contentTone: ['positive'] },
  },
  reflection: {
    color:    { tone: 'warm', brightness: 'low', saturation: 'low', accent: null },
    base:     'dark',
    ambient:  'warm_bokeh',
    pattern:  null,
    vfx:      ['film_grain'],
    emphasis: ['underline_sweep', 'floating_motes'],
    fits:     { mood: ['reflection', 'lesson', 'closing', 'message'], sceneType: ['M', 'VM', 'V'], contentTone: ['neutral', 'positive'] },
  },
  celebration: {
    color:    { tone: 'warm', brightness: 'high', saturation: 'high', accent: 'success' },
    base:     'light',
    ambient:  'particle_rise',  // 상승 입자 — celebration 무드 시각화
    pattern:  null,
    vfx:      ['white_flash'],
    emphasis: ['confetti', 'light_streak', 'underline_sweep'],
    fits:     { mood: ['success', 'celebration', 'achievement', 'finale'], sceneType: ['M', 'VM'], contentTone: ['positive'] },
  },
} as const;

// tone × base → BG 토큰 키 매핑
const _BG_BY_TONE_BASE: Record<SceneBase, Record<ColorTone, keyof typeof BG>> = {
  light: { neutral: 'cloud',  cool: 'iceBlue', warm: 'linen' },
  dark:  { neutral: 'slate',  cool: 'navy',    warm: 'dusk'  },
};

// 페르소나 ID → AtmosphereColors 해석. colors prop 미전달 시 기본 매핑 사용.
// 브랜드 토큰 분리 시: ACCENT_MAP과 _BG_BY_TONE_BASE만 브랜드별로 갈아끼우면 같은 페르소나가 다른 브랜드 색으로 렌더됨.
export const resolvePersonaColors = (id: PersonaId): AtmosphereColors => resolveSceneColors(id);

// ─── designSystem 소비 (변주 배선) ─────────────────────────────
// 디렉터가 출력하는 영상 전체 아트디렉션(§3-8)을 씬 색 해석에 반영한다.
// 원칙: progression 은 배경의 명도 envelope(base)만 조정하고 페르소나의 tone 개성은 유지 —
// 씬별 다양성(페르소나)과 영상 전체 아크(디렉터 설계)를 동시에 살린다.

export type MsbProgressionId = keyof typeof PROGRESSION;
export type MsbClimaxId = keyof typeof CLIMAX | 'flash_impact' | 'bloom_spread';
export type MsbAccentId = keyof typeof ACCENT;

export interface MsbDesignSystem {
  progression?: MsbProgressionId;
  climax?: MsbClimaxId;
  accent?: MsbAccentId;
}

export const ACCENT_VARIANTS = ['primary', 'danger', 'success', 'warning', 'muted'] as const;
export type AccentVariant = (typeof ACCENT_VARIANTS)[number];

// muted 변주 전용 — ACCENT 팔레트에 무채색이 없어 슬레이트 계열로 별도 정의.
const _MUTED_ACCENT: AccentPair = {
  onDark:  { accent: '#94A3B8', accentGlow: 'rgba(148,163,184,0.22)', accentDim: 'rgba(148,163,184,0.14)' },
  onLight: { accent: '#64748B', accentGlow: 'rgba(100,116,139,0.18)', accentDim: 'rgba(100,116,139,0.10)' },
};

// accentVariant → 의미 색 (primary 는 영상 accent/페르소나 유지라 여기 없음)
const _VARIANT_ACCENT: Partial<Record<AccentVariant, AccentPair>> = {
  danger:  ACCENT.deep_red,
  success: ACCENT.green,
  warning: ACCENT.amber,
  muted:   _MUTED_ACCENT,
};

export interface SceneColorCtx {
  design?: MsbDesignSystem;
  /** 씬 강조색 override (§3-8) — designSystem.accent 보다 우선. */
  accentVariant?: AccentVariant | null;
  /** dark_to_light 등 phase 분기 progression 의 씬 위상 — 없으면 페르소나 base 유지. */
  phase?: 'dark' | 'light' | null;
  /** A★ M 씬 — climax 색 처방(color_warmth accent·dark_light_flip 명도 반전) 적용 대상. */
  isClimax?: boolean;
}

/** 씬 컨텍스트(designSystem·accentVariant·phase·climax)를 반영한 색 해석 — resolvePersonaColors 의 일반화. */
export const resolveSceneColors = (id: PersonaId, ctx?: SceneColorCtx): AtmosphereColors => {
  const spec = PERSONA_CATALOG[id];
  let base: SceneBase = spec.base;
  const tone: ColorTone = ctx?.design?.progression === 'warm_throughout' ? 'warm' : spec.color.tone;
  switch (ctx?.design?.progression) {
    // P0 근사 계약: gradient_shift(색 흐름)·light_to_saturated(채도 상승)는 씬 위치 정보 없이는 완전 재현이
    // 불가해 명도 envelope 로만 근사한다(전자=dark 유지·후자=light 유지). 아크 내 미세 변화는 후속
    // (씬 fraction 전파) 후보 — PROGRESSION 팔레트 자체는 토큰에 준비돼 있음.
    case 'dark_sustained':
    case 'gradient_shift':
    case 'warm_throughout':
      base = 'dark';
      break;
    case 'light_sustained':
    case 'light_to_saturated':
      base = 'light';
      break;
    case 'dark_to_light':
    case 'light_to_dark':
      if (ctx?.phase === 'dark' || ctx?.phase === 'light') base = ctx.phase;
      break;
  }
  // climax 명도 반전 — 어둠 끝에 답이 켜지는 A★ (progression 보다 우선)
  if (ctx?.isClimax && ctx?.design?.climax === 'dark_light_flip') base = 'light';

  const bgKey = _BG_BY_TONE_BASE[base][tone];
  const isLight = base === 'light';
  let accentPair: AccentPair = spec.color.accent ? ACCENT_MAP[spec.color.accent] : ACCENT.blue;
  const designAccent = ctx?.design?.accent;
  // ★own-property 체크 — 'constructor' 류 prototype 키가 `in` 을 뚫고 들어와 Function 을 팔레트로 쓰면 크래시 (Codex 재현).
  if (designAccent && Object.prototype.hasOwnProperty.call(ACCENT, designAccent)) accentPair = ACCENT[designAccent];
  const variant = ctx?.accentVariant;
  // own-property 체크 — 타입 우회 직접 호출('constructor' 등)에도 크래시 금지 (Codex P1).
  if (variant && variant !== 'primary' && Object.prototype.hasOwnProperty.call(_VARIANT_ACCENT, variant)) {
    accentPair = _VARIANT_ACCENT[variant] as AccentPair;
  }
  // climax 온기 — A★ 의 결의·다짐 앰버 (CLIMAX.color_warmth 팔레트 계열)
  if (ctx?.isClimax && ctx?.design?.climax === 'color_warmth') accentPair = ACCENT.amber;
  const accentToken = (isLight ? accentPair.onLight : accentPair.onDark) as AccentSurface;
  return {
    bg:         (BG[bgKey] as ReturnType<typeof mkBg>).base,
    panel:      (BG[bgKey] as ReturnType<typeof mkBg>).panel,
    accent:     accentToken.accent,
    accentDim:  accentToken.accentDim,
    accentGlow: accentToken.accentGlow,
    isDark:     !isLight,
    textMain:   isLight ? TEXT.mainDark  : TEXT.mainLight,
    textSub:    isLight ? TEXT.subDark   : TEXT.subLight,
    status:     resolveStatus(!isLight),
  };
};

// MSB context로 호환 페르소나 필터링
// 잘못된 mood/sceneType/contentTone 조합은 메뉴에서 사라짐 — 원리 6 (컨텍스트 → 카탈로그 필터링)
export const filterPersonas = (ctx: {
  mood?: string;
  sceneType?: 'M' | 'VM' | 'V';
  contentTone?: string;
}): PersonaId[] => {
  return (Object.keys(PERSONA_CATALOG) as PersonaId[]).filter(id => {
    const f = PERSONA_CATALOG[id].fits;
    if (ctx.mood        && !f.mood.includes(ctx.mood))               return false;
    if (ctx.sceneType   && !f.sceneType.includes(ctx.sceneType))     return false;
    if (ctx.contentTone && !f.contentTone.includes(ctx.contentTone)) return false;
    return true;
  });
};
